// widget/src/FinChatWidget.tsx
//
// "Pearl — Messenger" variant: a second Pearl UI built to evaluate against
// the existing dark-glass ChatWidget, modelled on the Fin.ai chat.
//   - CLOSED: a centered bottom mini-input launcher you can type into.
//   - OPEN: a dark-glass panel with the conversation above a pinned composer.
// Reuses the exact same data layer (useChatStream / usePageContext /
// useProactive / useMobileSheet) — this is presentation only.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
} from "react"
import type { PageContext, WidgetMountOptions } from "./types"
import { FinMessage, Chips } from "./components/FinMessage"
import {
  useChatStream,
  type ConversationSummary,
  type OutgoingAttachment,
} from "./hooks/useChatStream"
import { usePageContext } from "./hooks/usePageContext"
import { useSyncPageContext } from "./hooks/useSyncPageContext"
import { useProactive } from "./hooks/useProactive"
import { useMobileSheet } from "./hooks/useMobileSheet"
import { useOpenTriggers } from "./hooks/useOpenTriggers"
import { useDrawerOpen } from "./hooks/useDrawerOpen"
import { IdentityCaptureForm } from "./components/IdentityCaptureForm"
import { CuratedEmojiPicker } from "./components/CuratedEmojiPicker"
import { CanvasShell } from "./components/CanvasShell"
import { BottomSheet, type BottomSheetHandle } from "./components/BottomSheet"
import { BasketDockChip } from "./components/BasketDockChip"
import { CanvasPayloadRouter } from "./components/CanvasPayloadRouter"
import { HistoryStrip } from "./components/HistoryStrip"
import { useMediaQuery } from "./hooks/useMediaQuery"
import { cn } from "./util"
import fm from "./styles/widgetFin.module.css"
import { CartContext } from "./cart-context"

// sessionStorage key — set after a successful identity capture so the gate
// doesn't re-show if the customer closes + reopens the widget within the
// same browser tab. We persist on the server too (chat_sessions.customer_*),
// but the local flag avoids a round-trip on every open.
const IDENTITY_CAPTURED_KEY = "dyc-pearl-identity-captured"

const WELCOME_TEXT =
  "Hi, I'm Pearl, DipYourCar's AI assistant — still in training, and I speak English & Spanish. Ask me about paint, sizing, OEM colors, or your order, and I'll bring in a human if I get stuck."

// A/B variants for the welcome experiment. The control matches WELCOME_TEXT
// exactly; the alternates test different opening framings. Variant assignment
// arrives from the server via /api/chat/experiments — see useExperiments.
// All variants disclose that Pearl is in training + bilingual (EN/ES).
const WELCOME_VARIANTS: Record<string, string> = {
  control: WELCOME_TEXT,
  curiosity:
    "Hey, I'm Pearl — still in training, and I speak English & Spanish. What are you painting? I can match factory colors, size your kit, or point you to a how-to, and tag in a human if anything gets tricky.",
  direct_help:
    "I'm Pearl, your DipYourCar paint assistant — still in training, and I speak English & Spanish. Tell me what you're working on (vehicle, color, project size) and I'll get you sorted in a minute.",
}
// New customer, general page (incl. homepage) — routing-aligned entry points.
// "Chat with the DYC team" intentionally OMITTED from first-turn chips
// (2026-06-03): Justin asked to minimise the human-handoff suggestion on
// first contact so new customers actually try Pearl before pivoting to a
// human. The handoff is still available anytime by typing "human"/"team"
// or via Pearl's own handoff routing.
const WELCOME_CHIPS = [
  "Find my car's color",
  "Build a car kit",
  "Peelable or permanent?",
  "Track my order",
]
// New customer on a product page — generic fallback for products that don't
// match any of the per-class buckets below. The class-specific chips render
// when the URL pattern matches one of WHEEL_CHIPS / PEELABLE_CHIPS /
// PERMANENT_CHIPS / PEARL_CHIPS / AEROSOL_CHIPS / TOPCOAT_CHIPS / OEM_CHIPS.
const PRODUCT_CHIPS = [
  "Will it match my car?",
  "Enough for my car?",
  "I'll take it",
]

// ── Per-product-class chip sets ─────────────────────────────────────────
//
// Each set is 3-4 chips tuned to what a customer browsing that product class
// most likely wants to know. "I'll take it" appears in every set as the
// universal buy-intent trigger — Pearl's system prompt routes that into
// show_product_card → confirm variant → add_to_cart.
//
// Matching is URL-pattern based (see chipsForProduct below). Good enough for
// v1 — long-term we'd promote to a per-product field on pearl_product_openers.

// Conversion formula: DE-RISK (the top objection for that class) → VALUE (the
// kit/bundle) → SIZING ("enough for my…?" = a buy-readiness check) → CLOSE
// ("I'll take it" → show_product_card → add_to_cart). Wheels LEAD with the kit
// (never loose cans). Chips reference only what Pearl can ALWAYS answer — NO
// "see it" media promises (the storefront has no guaranteed per-product video/
// photo; a chip that dead-ends at "I don't have that" kills trust — Justin
// 2026-06-14). Visual-proof chips return with Phase 2 (Proof Arsenal).
const WHEEL_CHIPS = [
  "Build my wheel kit",
  "Will it last on wheels?",
  "Enough for 4 wheels?",
  "I'll take it",
]
const PEELABLE_CHIPS = [
  "Is it easy to apply?",
  "How long does it last?",
  "Enough for my car?",
  "I'll take it",
]
const PERMANENT_CHIPS = [
  "Will it match my car?",
  "What's in the kit?",
  "Enough for my car?",
  "I'll take it",
]
const PEARL_CHIPS = [
  "How dramatic is the shift?",
  "What's in the kit?",
  "Enough for my car?",
  "I'll take it",
]
const AEROSOL_CHIPS = [
  "How many cans for my job?",
  "Is it easy to apply?",
  "I'll take it",
]
const TOPCOAT_CHIPS = [
  "What does it add?",
  "What goes under it?",
  "I'll take it",
]
const OEM_CHIPS = [
  "Will it match exactly?",
  "What's in the kit?",
  "Enough for my repair?",
  "I'll take it",
]

/**
 * Map a Shopify product handle to a chip set by URL pattern. Order matters
 * — `*-flip-*` and `*-dip-pearl*` are checked before the generic `*-aerosol`
 * fall-through because a flip topcoat IS sold as an aerosol but Pearl-class
 * chips are more useful than generic aerosol chips.
 */
function chipsForProduct(handle: string | undefined): string[] {
  if (!handle) return PRODUCT_CHIPS
  const h = handle.toLowerCase()
  // OEM / touch-up — checked first because OEM products often have brand
  // names in the handle that could collide with other classes.
  if (/touch-up|-oem-|^oem-/.test(h)) return OEM_CHIPS
  // Color-shift / DipPearls — checked before plain aerosols.
  if (/-flip-|-dip-pearl|-pearl-|hyper-?shift/.test(h)) return PEARL_CHIPS
  // Wheel kits.
  if (/wheel-kit|wheel_kit/.test(h)) return WHEEL_CHIPS
  // Permanent paint (Luna line + classic permanent product names).
  if (/^luna-|-basecoat|-clearcoat|-primer/.test(h)) return PERMANENT_CHIPS
  // Peelable family (HyperDip + Plasti Dip + generic peelable indicators).
  if (/hyperdip|plasti-?dip|peelable|hyper-dip/.test(h)) return PEELABLE_CHIPS
  // Generic topcoat (after the flip/pearl check above).
  if (/-topcoat|topcoat-/.test(h)) return TOPCOAT_CHIPS
  // Generic aerosol can.
  if (/-aerosol|aerosol-/.test(h)) return AEROSOL_CHIPS
  return PRODUCT_CHIPS
}
// Returning (logged-in) customer — lead with what they likely came back for.
// Human-handoff omitted on the first turn; see WELCOME_CHIPS for rationale.
const RETURNING_CHIPS = [
  "Track my order",
  "Help finding a color",
  "Start a new project",
]

// Phase 4 — proactive chip sets keyed to the behavioral trigger that fired.
// These reflect the new objection/sizing/match developments: lead with the
// de-risking question for THAT moment (fit, amount, see-it) rather than a
// generic "need help?". Used when a server-gated proactive nudge arrives;
// falls back to chipsForProduct() when the trigger has no specific set.
// Each moment leads with the de-risking move that converts THAT hesitation,
// then pushes toward the kit + the close. Proof / value / "enough?" / buy —
// never a dead-end research question.
const CHIPS_BY_TRIGGER: Record<string, string[]> = {
  product_dwell: ["Will it match my car?", "Enough for my car?", "I'll take it"],
  scroll_depth: ["What's in the kit?", "Enough for my car?", "I'll take it"],
  time_on_page: ["Help me pick my kit", "Enough for my car?", "Chat with the DYC team"],
  price_hesitation: ["Is it worth it?", "Cheaper than a body shop?", "What's included?"],
  exit_intent: ["Will it fit my car?", "Email me a quote", "I'll take it"],
  cart_abandon: ["Is my kit complete?", "Add the topcoat?", "Finish checkout"],
  repeat_page_view: ["Build my kit", "Enough for my car?", "I'll take it"],
  idle_reengage: ["Will it match my car?", "Enough for my car?", "Help me pick"],
}
const greetReturning = (firstName: string) =>
  `Welcome back, ${firstName}! Need a hand with an order, or starting something new?`
const PROACTIVE_FALLBACK_GENERIC =
  "Looking at this one? I can help with sizing, colors, or how to apply it."
// Homepage detection. The site root is "/", but Shopify storefronts can serve
// the home page under a locale prefix ("/en", "/en-us", "/fr-ca"). Treat a bare
// locale-only path as the homepage too, so the trigger fires on localized
// storefronts without false-firing on real content pages.
function isHomepagePath(path: string | undefined): boolean {
  if (!path) return false
  const clean = path.replace(/[?#].*$/, "").replace(/\/+$/, "") || "/"
  if (clean === "/") return true
  // "/en" or "/en-us" / "/fr-ca" — a single segment that's a locale code.
  return /^\/[a-z]{2}(-[a-z]{2})?$/i.test(clean)
}

// Customer wants to be left alone. Matching this in a sent message permanently
// dismisses the proactive opener for the session (see sendText).
const STOP_INTENT_RE =
  /\b(stop|leave me alone|go away|shut up|piss off|fuck off|unsubscribe)\b|stop (popping|messaging|texting|interrupting|contacting)|i'?m leaving/i

/**
 * Per-product fallback when there's no curated opener in pearl_product_openers.
 * Uses the title extracted from the page (og:title / document.title) so the
 * chip reads "Looking at the Anthracite Grey Wheel Kit? ..." instead of the
 * generic "this one" line. Falls back to the generic copy if extraction failed.
 */
function buildProactiveFallback(productTitle: string | undefined): string {
  if (!productTitle) return PROACTIVE_FALLBACK_GENERIC
  // Strip leading "The" / "A" / "An" from the title so we can prefix our own
  // "the" without "the The". Word-boundary truncate so super-long titles
  // never produce mid-word cuts like "...You Can Cou".
  const t = truncateAtWord(productTitle.replace(/^(the|a|an)\s+/i, ""), 50)
  return `Looking at the ${t}? I can help with sizing, color match, or application.`
}

/**
 * Word-boundary truncate. Returns the original string unchanged when it
 * fits; otherwise cuts at the last whitespace at-or-before `max` and
 * appends nothing (no "…" because the intro line reads better without).
 * Returns "" if there's no clean break before `max` (rare — would be one
 * giant token longer than the limit; we'd rather drop than disfigure).
 *
 * The 50-char default for the intro is calibrated for the bubble width on
 * mobile — at 60 chars the line wraps awkwardly with the surrounding
 * "I can help with the ... — first, what's your name and email?" text.
 */
function truncateAtWord(s: string, max: number): string {
  if (!s) return ""
  if (s.length <= max) return s
  const slice = s.slice(0, max)
  const lastSpace = slice.lastIndexOf(" ")
  if (lastSpace < 8) return "" // pathological — bail to generic intro
  return slice.slice(0, lastSpace).trim()
}
const LAUNCHER_PLACEHOLDER = "Ask Pearl about paint, colors, or your order…"

// Typewriter rotation — questions cycle through the placeholder so the
// input feels alive. Each phrase types in, holds, then deletes before
// the next one starts. The customer always sees "what kinds of things
// can I ask?" as an implicit demo. Genuine examples from real Pearl
// conversations so the suggestions read as natural use cases.
const PLACEHOLDER_ROTATION = [
  "What's my paint code?",
  "How much paint do I need?",
  "Best clearcoat for a Tesla?",
  "Track my order",
  "Peelable vs permanent?",
  "Show me Bonita Blue",
  "Can I paint over plastic?",
  "Help me build a kit",
]

const ArrowUp = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
)
const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
)
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
)
const ChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-9 8.32 8.5 8.5 0 0 1-3.9-.9L3 20l1.08-3.24A8.5 8.5 0 0 1 12 3.5a8.38 8.38 0 0 1 9 8z" />
  </svg>
)
const ImageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <circle cx="8.5" cy="8.5" r="1.6" />
    <path d="M21 15l-5-5L5 21" />
  </svg>
)
const VideoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="6" width="14" height="12" rx="2" />
    <path d="M17 10l4-2v8l-4-2z" />
  </svg>
)
const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <path d="M14 3v6h6" />
  </svg>
)
const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M17 8l-5-5-5 5" />
    <path d="M12 3v12" />
  </svg>
)
const XMark = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <path d="M6 6l12 12M6 18L18 6" />
  </svg>
)
const SmileyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <line x1="9" y1="9" x2="9.01" y2="9" />
    <line x1="15" y1="9" x2="15.01" y2="9" />
  </svg>
)
const HelpCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5a2.5 2.5 0 1 1 4 2c-.8.6-1.5 1-1.5 2" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
)
const DotsIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
)
/** Canvas/panel icon — used as the mobile chat-header toggle that opens
 *  the canvas BottomSheet to half height. Two stacked rounded rectangles
 *  read as "a panel with content underneath" without committing to a
 *  specific noun (basket vs results vs tracker), since the canvas
 *  surface hosts any of those. */
const CanvasPanelIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="10" rx="2.5" />
    <path d="M3 18.5h18" />
    <path d="M3 21h12" />
  </svg>
)

// Pearl's avatar: a glossy black→red orb. A diagonal body that rises from
// LIVING orb: the color gradient slowly rotates while its stops morph through a
// vivid spectrum (red, orange, magenta, violet, electric blue, cyan) over a dark
// glossy base. A fixed light-to-dark radial gives sphere volume, a white
// specular highlight adds gloss (gently breathing), and a faint
// rim so it reads on both the white chip and the dark header. `id` namespaces
// the gradient defs so the two instances (chip + header) don't collide.
const PearlOrb = ({ id, animated }: { id: string; animated: boolean }) => (
  <svg viewBox="0 0 100 100" className={fm["orb-svg"]} aria-hidden="true">
    <defs>
      <linearGradient
        id={`${id}-i`}
        gradientUnits="userSpaceOnUse"
        x1="14"
        y1="86"
        x2="86"
        y2="14"
      >
        {animated && (
          <animateTransform
            attributeName="gradientTransform"
            type="rotate"
            from="0 50 50"
            to="360 50 50"
            dur="18s"
            repeatCount="indefinite"
          />
        )}
        <stop offset="0%" stopColor="#1a0610">
          {animated && (
            <animate
              attributeName="stop-color"
              values="#1a0610;#1a0c04;#18041c;#0a0826;#04122a;#04201f;#1a0610"
              dur="16s"
              repeatCount="indefinite"
            />
          )}
        </stop>
        <stop offset="42%" stopColor="#8a0f26">
          {animated && (
            <animate
              attributeName="stop-color"
              values="#8a0f26;#8a3a0a;#7a1070;#3a1f9e;#123f9e;#0e7a76;#8a0f26"
              dur="15s"
              repeatCount="indefinite"
            />
          )}
        </stop>
        <stop offset="76%" stopColor="#e0123f">
          {animated && (
            <animate
              attributeName="stop-color"
              values="#e0123f;#e85a12;#d01a9e;#7a30e0;#1f6fe0;#16c2c2;#e0123f"
              dur="14s"
              repeatCount="indefinite"
            />
          )}
        </stop>
        <stop offset="100%" stopColor="#ff3a4d">
          {animated && (
            <animate
              attributeName="stop-color"
              values="#ff3a4d;#ff7a2e;#ff2db4;#a64dff;#3d7bff;#2ee6e6;#ff3a4d"
              dur="13s"
              repeatCount="indefinite"
            />
          )}
        </stop>
      </linearGradient>
      <radialGradient id={`${id}-v`} cx="34%" cy="28%" r="82%">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.8" />
        <stop offset="42%" stopColor="#ffffff" stopOpacity="0" />
        <stop offset="100%" stopColor="#000000" stopOpacity="0.55" />
      </radialGradient>
      <radialGradient id={`${id}-h`} cx="33%" cy="25%" r="24%">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="50" cy="50" r="49" fill={`url(#${id}-i)`} />
    <circle cx="50" cy="50" r="49" fill={`url(#${id}-v)`} />
    <ellipse cx="35" cy="29" rx="15" ry="10" fill={`url(#${id}-h)`}>
      {animated && (
        <animate
          attributeName="opacity"
          values="1;0.72;1"
          dur="5s"
          repeatCount="indefinite"
        />
      )}
    </ellipse>
    <circle
      cx="50"
      cy="50"
      r="49"
      fill="none"
      stroke="#ffffff"
      strokeOpacity="0.22"
      strokeWidth="1.2"
    />
  </svg>
)

const MAX_IMAGES = 4
const MAX_IMAGE_DIM = 1568 // Claude's optimal long-edge; also keeps uploads small.

/** Resize + re-encode to JPEG client-side: shrinks multi-MB phone photos and
 *  guarantees a Claude-vision-compatible format (HEIC in → JPEG out). */
async function resizeImageToJpeg(file: File): Promise<Blob> {
  const dataUrl = await new Promise<string>((res, rej) => {
    const r = new FileReader()
    r.onload = () => res(r.result as string)
    r.onerror = () => rej(new Error("read failed"))
    r.readAsDataURL(file)
  })
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const im = new Image()
    im.onload = () => res(im)
    im.onerror = () => rej(new Error("decode failed"))
    im.src = dataUrl
  })
  let w = img.naturalWidth || img.width
  let h = img.naturalHeight || img.height
  if (Math.max(w, h) > MAX_IMAGE_DIM) {
    const scale = MAX_IMAGE_DIM / Math.max(w, h)
    w = Math.round(w * scale)
    h = Math.round(h * scale)
  }
  const canvas = document.createElement("canvas")
  canvas.width = w
  canvas.height = h
  const cctx = canvas.getContext("2d")
  if (!cctx) throw new Error("canvas unavailable")
  cctx.drawImage(img, 0, 0, w, h)
  const blob = await new Promise<Blob | null>((res) =>
    canvas.toBlob((b) => res(b), "image/jpeg", 0.85),
  )
  if (!blob) throw new Error("encode failed")
  return blob
}

/** "3h ago" / "2d ago" / "just now" — compact relative time for the history list. */
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ""
  const diff = Math.max(0, Date.now() - then)
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(then).toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

interface Props {
  mountOptions: WidgetMountOptions
  sessionToken: string
  initialContext: PageContext
  dockLabel?: string
  /** Server-assigned A/B variant per experiment slug. From
   *  /api/chat/experiments at mount time. Falls back to control behavior
   *  for any slug not present here. */
  experiments?: Record<string, string>
  /** When true, Pearl mini-cart canvas is rendered alongside the chat
   *  panel and rich-card actions route to it instead of inline bubbles.
   *  Gated by the `dyc-pearl-canvas` localStorage flag at mount time. */
  canvasEnabled?: boolean
  /** When true, a small one-time CSAT (1–5 star) prompt shows after a
   *  meaningfully-engaged chat is closed/resolved. OFF by default — when
   *  false NOTHING extra renders, so the default experience is unchanged.
   *  Sourced from messaging_mode_config.csat_enabled via the session
   *  handshake; operator-toggled in /chat/settings. */
  csatEnabled?: boolean
  /** Server-hydrated latest conversation. When present, the widget skips
   *  the empty welcome thread on mount and rehydrates this thread instead
   *  — so a customer who refreshes (or opens in a new tab) sees their
   *  in-flight chat continue without having to pick from the dropdown. */
  resumeConversation?: { id: string; messages: unknown[] } | null
}

export function FinChatWidget(props: Props) {
  const [active, setActive] = useState(false)
  // Preview state: proactive content fires (Pearl wants to suggest things)
  // but the customer hasn't actually engaged yet. Shows the input pill +
  // a chips strip above it WITHOUT opening the full conversation panel.
  // Modeled on Fin's "click the input → chips appear above" pattern —
  // lower-commitment than opening the whole container.
  // Transitions to active=true when the customer types or taps a chip.
  const [preview, setPreview] = useState(false)
  // True while the proactive stack plays its reverse-morph exit (chips
  // retract into the chip, chip glides back to center) before we actually
  // tear it down. Keeps the suggestions mounted for the ~220ms exit.
  const [stackClosing, setStackClosing] = useState(false)
  const stackCloseTimer = useRef<number | null>(null)
  // Chip text queued behind the identity gate. When a preview chip is
  // tapped on an anonymous session, we open the panel into the gate
  // FIRST so the customer captures name/email — THEN send the chip's
  // message. Without this, tapping a chip silently bypassed the gate
  // because sendText() pushed a customer-message row, which made
  // messages.length > 0 → gate condition false → no form ever rendered.
  const [pendingChipText, setPendingChipText] = useState<string | null>(null)
  // Set when the customer entered via a proactive chip → answer first, defer the
  // identity gate to a soft post-value ask (see gateReady / handlePreviewChipTap).
  const [valueFirstEntry, setValueFirstEntry] = useState(false)
  // Proactive "Prefer to text?" handoff card — shown once after the first
  // exchange, dismissible. Persisted per session so it doesn't re-nag on
  // refresh; respects the global proactive mute ("stop means stop").
  const [handoffCardDismissed, setHandoffCardDismissed] = useState(
    () =>
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem("pearl-handoff-dismissed") === "1",
  )
  // One-shot latch for the proactive card. Deriving visibility straight from
  // `!pending` made it flicker every turn (pending toggles as Pearl streams —
  // Justin 2026-06-16). Instead we ARM it once after the first exchange and
  // RETIRE it when the customer continues past that point — a true one-time
  // nudge that never flaps. The always-on header bubble covers later moments.
  const handoffArmCountRef = useRef(0)
  const [handoffVisible, setHandoffVisible] = useState(false)
  const [proactiveText, setProactiveText] = useState<string | null>(null)
  // Curated quick-reply chips from pearl_product_openers.chips. When null,
  // we fall through to chipsForProduct() (URL-pattern classifier). See the
  // welcomeChips assignment further down for the full precedence chain.
  const [curatedChips, setCuratedChips] = useState<string[] | null>(null)
  const [draft, setDraft] = useState("")
  const [menuOpen, setMenuOpen] = useState(false)
  const [attachMenuOpen, setAttachMenuOpen] = useState(false)
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [convosLoading, setConvosLoading] = useState(false)
  const [showScrollBtn, setShowScrollBtn] = useState(false)
  const [pendingImages, setPendingImages] = useState<OutgoingAttachment[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [emojiMenuOpen, setEmojiMenuOpen] = useState(false)
  // ── Post-resolution CSAT prompt ──────────────────────────────────────
  // Operator-gated (props.csatEnabled, OFF by default). When the customer
  // closes a meaningfully-engaged chat (>= 2 messages), a small star-rating
  // card surfaces near the launcher. Shown ONCE per conversation — guarded
  // by both a ref (this mount) and sessionStorage (survives reopen in-tab).
  // `csatConvId` pins the prompt to the conversation it was raised for so a
  // late submit can't mis-attribute the rating. null = no prompt showing.
  const [csatConvId, setCsatConvId] = useState<string | null>(null)
  const [csatScore, setCsatScore] = useState<number | null>(null)
  const [csatHover, setCsatHover] = useState<number | null>(null)
  const [csatComment, setCsatComment] = useState("")
  const [csatSubmitting, setCsatSubmitting] = useState(false)
  const [csatDone, setCsatDone] = useState(false)
  // Conversation ids we've already prompted this mount — belt-and-suspenders
  // alongside the per-conversation sessionStorage flag.
  const csatPromptedRef = useRef<Set<string>>(new Set())
  // Disclaimer sheet — opens from the (?) icon next to "Beta" in the header.
  // Covers DYC against misinformation complaints by being transparent about
  // Pearl's limits + giving customers a human-handoff path.
  const [helpOpen, setHelpOpen] = useState(false)
  // "Text us" header menu (mobile) — carries the current chat to iMessage or
  // WhatsApp via a minted handoff token, so mobile customers can shop from
  // their messages app instead of staying glued to the site (2026-06-10).
  const [textUsOpen, setTextUsOpen] = useState(false)
  const [textUsBusy, setTextUsBusy] = useState<string | null>(null)
  // Cart-add failure surface (audit 2026-06-11): /cart/add.js can 422 or
  // drop AFTER Pearl already said "Added" — without UI the customer reaches
  // checkout empty-handed. The side-effect handlers dispatch
  // dyc-pearl:cart-add-failed; we render a retry banner above the launcher.
  const [cartAddError, setCartAddError] = useState<
    | { items: Array<{ id: string; quantity: number }> }
    | null
  >(null)
  useEffect(() => {
    const onFail = (e: Event) => {
      const detail = (e as CustomEvent).detail as Record<string, unknown> | undefined
      const raw = Array.isArray(detail?.items)
        ? (detail!.items as Array<Record<string, unknown>>)
        : detail?.variantId
          ? [{ id: detail.variantId, quantity: detail.quantity ?? 1 }]
          : []
      const items = raw
        .map((it) => ({ id: String(it.id ?? ""), quantity: Number(it.quantity ?? 1) || 1 }))
        .filter((it) => it.id)
      if (items.length > 0) setCartAddError({ items })
    }
    window.addEventListener("dyc-pearl:cart-add-failed", onFail)
    return () => window.removeEventListener("dyc-pearl:cart-add-failed", onFail)
  }, [])
  const retryCartAdd = useCallback(async () => {
    if (!cartAddError) return
    try {
      const r = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items: cartAddError.items }),
      })
      if (r.ok) {
        setCartAddError(null)
        window.dispatchEvent(new CustomEvent("dyc-pearl:cart-retry-succeeded"))
      }
    } catch {
      /* keep the banner — they can retry again */
    }
  }, [cartAddError])

  const pageContext = usePageContext({
    customerEmail: props.initialContext.customerEmail,
    customerId: props.initialContext.customerId,
  })
  // Push every page navigation to the server in real-time so Pearl's
  // get_customer_context tool, proactive triggers, and the dashboard all
  // reflect the page the customer is on right now — not the page they were
  // on when they last sent a chat message. (The useSyncPageContext call lives
  // below useProactive so its onProactive handler can reference `messages` +
  // the same preview-strip handler the opener uses.)
  const {
    messages,
    pending,
    send,
    hadPersistedHistory,
    clearPersisted,
    newConversation,
    listConversations,
    loadConversation,
    currentConversationId,
    canvas,
    setActivePayload,
    closeCanvas,
    openCanvas,
    updateBasketSilent,
    commitBasketSilent,
    addToShopifyCart,
  } = useChatStream({
    apiBaseUrl: props.mountOptions.apiBaseUrl,
    sessionToken: props.sessionToken,
    pageContext,
    canvasEnabled: props.canvasEnabled,
  })

  const bodyRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  // Throttle the "customer is typing" ping → at most once every ~2s while the
  // customer types (best-effort presence signal for the operator console).
  const lastTypingPingRef = useRef(0)
  // Header-button refs — when a menu/sheet is dismissed via Escape, focus
  // returns to the button that opened it (keyboard users otherwise land
  // back on <body> and lose their place).
  const helpBtnRef = useRef<HTMLButtonElement>(null)
  const menuBtnRef = useRef<HTMLButtonElement>(null)
  const textUsBtnRef = useRef<HTMLButtonElement>(null)

  // ── Canvas close morph ────────────────────────────────────────────────
  // Closing the canvas can't be a CSS-only transition: flipping
  // `canvas.isOpen` false drops the `.layout-with-canvas` class AND unmounts
  // the CanvasShell on the SAME render tick, so the unified container
  // vanishes with no exit frame ("snaps" shut). Hold the open state for one
  // animation cycle: `canvasClosing` adds `.layout-closing` (reverse-morph
  // keyframes that shrink the container back to the input-pill width), then a
  // timer flips isOpen false to unmount once the morph has played. The 300ms
  // matches fm-layout-morph-out in widgetFin.module.css — keep them in sync.
  const [canvasClosing, setCanvasClosing] = useState(false)
  const canvasCloseTimer = useRef<number | null>(null)
  const requestCanvasClose = useCallback(() => {
    if (canvasCloseTimer.current != null) return // already closing
    setCanvasClosing(true)
    canvasCloseTimer.current = window.setTimeout(() => {
      closeCanvas()
      setCanvasClosing(false)
      canvasCloseTimer.current = null
    }, 300)
  }, [closeCanvas])
  useEffect(
    () => () => {
      if (canvasCloseTimer.current != null)
        window.clearTimeout(canvasCloseTimer.current)
    },
    [],
  )

  // ── Typewriter placeholder ────────────────────────────────────────────
  // Rotates through PLACEHOLDER_ROTATION with a type-pause-erase cycle.
  // Only animates while the customer hasn't typed anything (draft empty)
  // and the input isn't focused — once they engage, the placeholder is
  // either irrelevant (they're typing) or distracting. Falls back to the
  // static LAUNCHER_PLACEHOLDER on prefers-reduced-motion.
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState(
    PLACEHOLDER_ROTATION[0],
  )
  // Pause animation in any state where the customer wouldn't see the
  // rotating placeholder — saves ~18 re-renders/sec when the chat is
  // open or the customer is mid-message.
  const placeholderActive = !active && draft.length === 0
  useEffect(() => {
    if (!placeholderActive) return
    if (typeof window === "undefined") return
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setAnimatedPlaceholder(LAUNCHER_PLACEHOLDER)
      return
    }
    let phraseIdx = 0
    let charIdx = 0
    let phase: "typing" | "hold" | "erasing" = "typing"
    let timer: ReturnType<typeof setTimeout> | null = null
    let cancelled = false
    const TYPE_MS = 55 // ms per character on the way in
    const ERASE_MS = 25 // erase faster than typing — feels punchier
    const HOLD_MS = 1600 // pause at the fully-typed phrase
    const tick = () => {
      if (cancelled) return
      const phrase = PLACEHOLDER_ROTATION[phraseIdx]
      if (phase === "typing") {
        charIdx++
        setAnimatedPlaceholder(phrase.slice(0, charIdx))
        if (charIdx >= phrase.length) {
          phase = "hold"
          timer = setTimeout(tick, HOLD_MS)
          return
        }
        timer = setTimeout(tick, TYPE_MS)
        return
      }
      if (phase === "hold") {
        phase = "erasing"
        timer = setTimeout(tick, ERASE_MS)
        return
      }
      // erasing
      charIdx--
      setAnimatedPlaceholder(phrase.slice(0, charIdx))
      if (charIdx <= 0) {
        phaseIdx_next()
        phase = "typing"
      }
      timer = setTimeout(tick, ERASE_MS)
    }
    const phaseIdx_next = () => {
      phraseIdx = (phraseIdx + 1) % PLACEHOLDER_ROTATION.length
      charIdx = 0
    }
    timer = setTimeout(tick, 600) // initial delay before first type
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [placeholderActive])
  // The two pieces of the open chat (panel + the detached input pill). A
  // pointerdown outside BOTH minimises — see the click-outside effect. This is
  // independent of the scrim element, so it works even where the scrim isn't
  // catching clicks.
  const panelRef = useRef<HTMLDivElement>(null)
  const launcherRef = useRef<HTMLDivElement>(null)
  // Whether the message list is scrolled to (or near) the bottom. Drives both
  // the scroll-to-bottom button and the auto-scroll gate — when the user has
  // scrolled up to read history, new tokens must NOT yank them back down.
  const atBottomRef = useRef(true)

  const handleProactiveFire = useCallback(
    (opener: string | null, chips: string[] | null) => {
      // Curated opener wins. Otherwise build a product-aware fallback from
      // the page title; finally fall back to the fully generic line if even
      // that's missing.
      setProactiveText(opener ?? buildProactiveFallback(pageContext.productTitle))
      // Curated chips win over the URL-pattern classifier when set.
      setCuratedChips(chips)
      // Open into PREVIEW (chips strip above input pill) rather than the
      // full container. Customer engagement (typing or chip tap) is the
      // explicit handoff into the full open state. Lower-commitment than
      // the old setActive(true) which slammed the whole panel open the
      // moment Pearl had something to say.
      setPreview(true)
    },
    [pageContext.productTitle],
  )
  const { dismissProactive } = useProactive({
    apiBaseUrl: props.mountOptions.apiBaseUrl,
    pageContext,
    active,
    hasEngaged: messages.length > 0,
    onFire: handleProactiveFire,
  })

  // Dev/test only: `?pearl_force_stack=1` fires the proactive stack immediately
  // on mount, bypassing the 6s dwell + the reduced-motion / dismiss / once-per
  // gates in useProactive — so the morph is always demoable on the local
  // harness without waiting or fighting the trigger. No effect without the flag.
  useEffect(() => {
    if (typeof window === "undefined") return
    if (
      new URLSearchParams(window.location.search).get("pearl_force_stack") !==
      "1"
    )
      return
    const handle = pageContext.productHandle
    const t = window.setTimeout(async () => {
      let opener: string | null = null
      let chips: string[] | null = null
      if (handle) {
        try {
          const r = await fetch(
            `${props.mountOptions.apiBaseUrl}/api/chat/opener?handle=${encodeURIComponent(handle)}`,
          )
          if (r.ok) {
            const b = (await r.json()) as {
              opener?: string | null
              chips?: string[] | null
            }
            opener = b.opener ?? null
            if (Array.isArray(b.chips) && b.chips.length >= 2) chips = b.chips
          }
        } catch {
          /* fall back to the product-class chips */
        }
      }
      handleProactiveFire(opener, chips)
    }, 400)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Homepage proactive auto-open REMOVED (2026-06-28, per request): Pearl must
  // not pop on the site root. The launcher still shows there — visitors open her
  // by clicking. Product-page dwell (useProactive) is unaffected; server nudges
  // are also gated off the homepage in handleProactiveNudge below.

  // Phase 4 — a server-gated proactive nudge (chat_triggers + restraint ledger)
  // arrives on the /api/chat/context response. Render it through the SAME
  // preview-strip path the opener uses, with the same client-side gates so it
  // never overrides an open/engaged conversation or a dismissed strip. The
  // SERVER already applied caps/cooldowns/opt-out + the enable flag, so this is
  // the last, lightweight guard.
  const handleProactiveNudge = useCallback(
    (nudge: { message: string; triggerType?: string }) => {
      if (active || messages.length > 0) return
      // No proactive auto-open on the homepage (per request 2026-06-28) — even
      // if the server returns a nudge, the site root stays clean.
      if (isHomepagePath(pageContext.currentPage)) return
      if (
        typeof sessionStorage !== "undefined" &&
        sessionStorage.getItem("pearl-proactive-dismissed") === "1"
      )
        return
      if (proactiveText) return // an opener/nudge is already showing
      // Trigger-aware chips (fit/amount/see-it for the moment that fired);
      // null → handleProactiveFire falls through to chipsForProduct.
      const chips =
        (nudge.triggerType && CHIPS_BY_TRIGGER[nudge.triggerType]) || null
      handleProactiveFire(nudge.message, chips)
    },
    [
      active,
      messages.length,
      proactiveText,
      handleProactiveFire,
      pageContext.currentPage,
    ],
  )
  useSyncPageContext(
    props.mountOptions.apiBaseUrl,
    props.sessionToken,
    pageContext,
    handleProactiveNudge,
  )

  useMobileSheet(active)
  // Hide the chip while a storefront drawer (cart, nav-finder, etc.) is open —
  // same rule as the Shopify chat (theme: assets/shopify-chat-fix.js).
  const drawerOpen = useDrawerOpen()
  // Phase 2 mini-cart: pick the bottom-sheet branch on mobile, desktop
  // CanvasShell otherwise. 767px matches the existing mobile CSS breakpoint
  // (see widgetFin.module.css @media query for .root.open).
  const isMobile = useMediaQuery("(max-width: 767px)")
  // Ref into BottomSheet so the BasketDockChip tap can call expand("half")
  // — programmatic state changes without lifting the entire FSM up here.
  const bottomSheetRef = useRef<BottomSheetHandle>(null)

  // ── Auto-resume the latest conversation across page refresh / nav ──
  // The session resume handshake (widget/src/index.tsx) ships the latest
  // conversation id + messages when a known session token returns. Without
  // this effect, the widget would mount with an empty thread and the
  // customer would have to open the "Conversations" dropdown and pick
  // the chat they were just having — caught 2026-06-03 when Justin
  // reported "when page is refreshed or you navigate to a new page the
  // chat must persist". Fires once on mount; subsequent renders skip via
  // the ref guard so a stale resume can't clobber an in-flight new chat.
  /** Strip suppression for the CURRENT page: session-wide stop ("stop
   *  popping up" / non-product X) or this product's own X. */
  const stripDismissedHere = useCallback((): boolean => {
    try {
      if (sessionStorage.getItem("pearl-proactive-dismissed") === "1") return true
      const handle = pageContext.productHandle
      return !!handle && sessionStorage.getItem("pearl-strip-dismissed:" + handle) === "1"
    } catch {
      return true
    }
  }, [pageContext.productHandle])

  const resumedRef = useRef(false)
  useEffect(() => {
    if (resumedRef.current) return
    const resume = props.resumeConversation
    if (!resume?.id || !resume.messages.length) return
    if (messages.length > 0) return // a fresh session already wrote something
    resumedRef.current = true
    loadConversation(resume.id)
    // Load the thread SILENTLY — do NOT auto-open the panel. Slamming the
    // full widget open on every page change was the #1 annoyance (Justin
    // 2026-06-10: "the widget is still loading up on pages as you open them
    // on its own"). Instead surface the light preview-chip strip (Justin
    // 2026-06-09: "I'd much rather use the preview chip strip popup when a
    // customer changes page vs the main widget") — unless they dismissed it
    // this session, in which case stay a bare orb. Tapping the orb/a chip/
    // the input opens the panel with the thread already loaded.
    // Not on the homepage — the site root stays clean (per request 2026-06-28).
    // Other pages still get the light resume strip for returning visitors.
    if (!stripDismissedHere() && !isHomepagePath(pageContext.currentPage))
      setPreview(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Mobile BottomSheet auto-open on new canvas content ─────────────
  // Mirrors desktop's auto-open: when Pearl surfaces a new payload
  // (product results, color match, basket update, tracking card), the
  // sheet snaps to "half" so the customer immediately sees the result
  // without having to know to tap a peek-state chip. Only fires when:
  //   - We're on mobile (otherwise CanvasShell handles it)
  //   - Canvas feature is on
  //   - A NEW payload arrived (payload.id changed) — not on every render
  // Skips the auto-expand if the customer already pulled the sheet to
  // "full" so we don't fight their intent.
  const lastSheetPayloadIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isMobile || !props.canvasEnabled) return
    const nextId = canvas.activePayload?.id ?? null
    if (!nextId) return
    if (nextId === lastSheetPayloadIdRef.current) return
    lastSheetPayloadIdRef.current = nextId
    const sheet = bottomSheetRef.current
    if (!sheet) return
    if (sheet.state === "full") return
    sheet.expand("half")
  }, [canvas.activePayload?.id, isMobile, props.canvasEnabled])

  // Track scroll position → button visibility + auto-scroll gate.
  const handleBodyScroll = useCallback(() => {
    const el = bodyRef.current
    if (!el) return
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight
    const atBottom = distance < 80
    atBottomRef.current = atBottom
    // Only offer the button when there's something to scroll to.
    setShowScrollBtn(!atBottom && el.scrollHeight - el.clientHeight > 60)
  }, [])

  const scrollToBottom = useCallback((smooth = true) => {
    const el = bodyRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" })
    atBottomRef.current = true
    setShowScrollBtn(false)
  }, [])

  // Auto-scroll on new content — but only if the user is already at the
  // bottom, so reading earlier messages isn't interrupted by streaming tokens.
  useEffect(() => {
    if (!atBottomRef.current) {
      // Still re-evaluate the button (content grew above the fold).
      handleBodyScroll()
      return
    }
    const el = bodyRef.current
    if (!el) return
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight
    })
  }, [messages, pending, handleBodyScroll])

  // Mobile keyboard re-pin: when the soft keyboard opens, the panel's
  // available height shrinks (--pearl-kb-inset gets subtracted from
  // bottom), but the messages container's scrollTop doesn't move — so
  // the last streaming token can slip out of view, behind the keyboard.
  // The auto-scroll effect above doesn't re-fire because messages/pending
  // didn't change. We listen to visualViewport.resize directly and
  // re-pin to bottom IFF the user was already at the bottom (same
  // discipline as the content-driven autoscroll — never yank them away
  // from reading earlier messages). Caught 2026-06-03 when Justin asked
  // "when the keyboard is open the text streaming is at the bottom so
  // the users see the most recent text".
  useEffect(() => {
    if (!active) return
    if (typeof window === "undefined") return
    const vv = window.visualViewport
    if (!vv) return
    function onResize() {
      if (!atBottomRef.current) return
      const el = bodyRef.current
      if (!el) return
      // rAF lets the panel's bottom transition settle one frame before
      // we measure scrollHeight, so we don't pin to a stale layout.
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight
      })
    }
    vv.addEventListener("resize", onResize)
    return () => vv.removeEventListener("resize", onResize)
  }, [active])

  // Stable handle to the latest maybeRaiseCsat — lets minimise() (declared
  // above maybeRaiseCsat) trigger the prompt without a use-before-declaration
  // cycle or churning minimise's deps each render. Assigned just below where
  // maybeRaiseCsat is defined.
  const maybeRaiseCsatRef = useRef<() => void>(() => {})

  // Minimise: collapse to the chip but KEEP the conversation (don't clear the
  // persisted thread). Used for click-outside + Escape — distinct from the X,
  // which fully closes. We drop the "was open" flag so it doesn't auto-reopen
  // on the next page; the thread is still there when the chip is tapped again.
  const minimise = useCallback(() => {
    // Click-outside / Escape on an engaged chat is also a "done" signal —
    // raise the CSAT prompt (no-op when disabled / too short / already asked).
    maybeRaiseCsatRef.current()
    setActive(false)
    setPreview(false)
    // Collapse the canvas TOO — otherwise click-away hid the chat (input +
    // chips) but left the canvas pane/sheet floating open: a visible desync
    // (Justin 2026-06-16). Everything dismisses together now. No-op when the
    // canvas is already closed.
    closeCanvas()
    setMenuOpen(false)
    setAttachMenuOpen(false)
    setEmojiMenuOpen(false)
    setHelpOpen(false)
    // A chip tapped into the identity gate queues its text; abandoning the
    // gate must drop it or the stale text fires after a LATER capture
    // (audit 2026-06-11).
    setPendingChipText(null)
    try {
      sessionStorage.removeItem("pearl-was-open")
    } catch {
      /* ignore */
    }
  }, [closeCanvas])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return
      // Closing a menu/sheet returns focus to its trigger button so
      // keyboard users don't get dumped back onto <body>.
      if (helpOpen) {
        setHelpOpen(false)
        helpBtnRef.current?.focus()
      } else if (textUsOpen) {
        setTextUsOpen(false)
        textUsBtnRef.current?.focus()
      } else if (emojiMenuOpen) setEmojiMenuOpen(false)
      else if (attachMenuOpen) setAttachMenuOpen(false)
      else if (menuOpen) {
        setMenuOpen(false)
        menuBtnRef.current?.focus()
      } else if (active) minimise()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [active, menuOpen, attachMenuOpen, emojiMenuOpen, helpOpen, textUsOpen, minimise])

  // Click-outside → minimise. Listens at the document level rather than on the
  // scrim, so it fires even if the scrim isn't catching the click (host CSS,
  // stale styling, etc.). A press anywhere that isn't inside the panel, the
  // input pill, OR the canvas/sheet surfaces collapses Pearl to the chip
  // while preserving the conversation.
  //
  // Canvas/sheet exemption added 2026-06-03: without it, scrolling the
  // right-side canvas (desktop) or mobile BottomSheet fired pointerdown on
  // canvas elements, which fell outside panelRef/launcherRef and collapsed
  // the entire widget back to the chip mid-scroll. Customers had to re-tap
  // Pearl to continue — caught when Justin reported "when scrolling the
  // canvas the chat input hides and then to scroll or click in the chat on
  // the left you need to click the pearl chip — thats annoying".
  useEffect(() => {
    if (!active) return
    function onPointerDown(e: PointerEvent) {
      const t = e.target as Element | null
      if (!t) return
      if (panelRef.current?.contains(t)) return
      if (launcherRef.current?.contains(t)) return
      // Canvas pane (desktop) — see CanvasShell.tsx
      if (t.closest && t.closest('aside[aria-label="Pearl canvas"]')) return
      // BottomSheet (mobile) — see BottomSheet.tsx
      if (t.closest && t.closest('[role="region"][aria-label="Pearl basket sheet"]')) return
      minimise()
    }
    // Defer one frame so the same press that opened Pearl doesn't immediately
    // close it.
    const id = window.setTimeout(
      () => document.addEventListener("pointerdown", onPointerDown, true),
      0,
    )
    return () => {
      window.clearTimeout(id)
      document.removeEventListener("pointerdown", onPointerDown, true)
    }
  }, [active, minimise])

  // Keep the input focused once the panel opens.
  useEffect(() => {
    if (active) {
      const t = setTimeout(() => inputRef.current?.focus(), 120)
      return () => clearTimeout(t)
    }
  }, [active])

  // Kill any focus outline/border the host storefront's CSS bleeds onto the
  // textarea (the widget has no shadow root). Inline !important sits at the
  // top of the cascade and beats any host stylesheet regardless of specificity.
  useEffect(() => {
    const ta = inputRef.current
    if (!ta) return
    ta.style.setProperty("outline", "none", "important")
    ta.style.setProperty("border", "none", "important")
    ta.style.setProperty("box-shadow", "none", "important")
    ta.style.setProperty("-webkit-appearance", "none", "important")
    ta.style.setProperty("-webkit-tap-highlight-color", "transparent", "important")
  }, [])

  // Phase 4 — report a proactive nudge's outcome to the restraint ledger so
  // dismiss-kill + PERSISTENT cross-session opt-out actually work. Fire-and-forget.
  const reportProactiveOutcome = useCallback(
    (outcome: "dismissed" | "opted_out" | "engaged") => {
      try {
        fetch(`${props.mountOptions.apiBaseUrl}/api/chat/proactive/outcome`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionToken: props.sessionToken, outcome }),
          keepalive: true,
        }).catch(() => {})
      } catch {
        /* ignore */
      }
    },
    [props.mountOptions.apiBaseUrl, props.sessionToken],
  )

  // CSAT once-per-conversation guard key. Per-conversation so a customer who
  // has two separate chats can be asked about each — but never twice for one.
  const csatStorageKey = useCallback(
    (convId: string) => `dyc-pearl-csat:${convId}`,
    [],
  )

  // Raise the CSAT prompt when a meaningfully-engaged conversation closes —
  // IFF the operator enabled it. No-ops entirely when csatEnabled is false,
  // when the thread is too short to be "a chat" (< 2 messages), when there's
  // no conversation id yet, or when this conversation was already rated /
  // prompted (ref + sessionStorage). Called from both close() and minimise().
  const maybeRaiseCsat = useCallback(() => {
    if (!props.csatEnabled) return
    if (messages.length < 2) return
    const convId = currentConversationId.current
    if (!convId) return
    if (csatPromptedRef.current.has(convId)) return
    try {
      if (sessionStorage.getItem(csatStorageKey(convId)) === "1") return
    } catch {
      /* sessionStorage blocked — fall back to the ref guard only */
    }
    csatPromptedRef.current.add(convId)
    // Fresh prompt for this conversation — reset any stale form state.
    setCsatScore(null)
    setCsatHover(null)
    setCsatComment("")
    setCsatDone(false)
    setCsatConvId(convId)
  }, [props.csatEnabled, messages.length, currentConversationId, csatStorageKey])
  maybeRaiseCsatRef.current = maybeRaiseCsat

  // Mark this conversation handled (rated or dismissed) so it never re-prompts.
  const dismissCsat = useCallback(() => {
    const convId = csatConvId
    setCsatConvId(null)
    if (!convId) return
    try {
      sessionStorage.setItem(csatStorageKey(convId), "1")
    } catch {
      /* ignore — the ref guard still prevents a re-prompt this mount */
    }
  }, [csatConvId, csatStorageKey])

  const submitCsat = useCallback(
    async (score: number) => {
      const convId = csatConvId
      if (!convId || csatSubmitting) return
      setCsatScore(score)
      setCsatSubmitting(true)
      try {
        const r = await fetch(`${props.mountOptions.apiBaseUrl}/api/chat/csat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionToken: props.sessionToken,
            conversationId: convId,
            score,
            comment: csatComment.trim() || undefined,
          }),
          keepalive: true,
        })
        if (r.ok) {
          // Persist the once-per-conversation flag, show the thank-you, then
          // fade the card out after a beat.
          try {
            sessionStorage.setItem(csatStorageKey(convId), "1")
          } catch {
            /* ignore */
          }
          setCsatDone(true)
          window.setTimeout(() => setCsatConvId(null), 2200)
        } else {
          // Failure: let them try again — keep the card up, drop the spinner.
          console.warn("[dyc-pearl] csat submit failed", r.status)
        }
      } catch (err) {
        console.warn("[dyc-pearl] csat submit error", err)
      } finally {
        setCsatSubmitting(false)
      }
    },
    [
      csatConvId,
      csatSubmitting,
      csatComment,
      csatStorageKey,
      props.mountOptions.apiBaseUrl,
      props.sessionToken,
    ],
  )

  const open = useCallback(() => {
    setActive(true)
    // Transitioning into the full open state — preview's job is done.
    setPreview(false)
  }, [])
  const close = useCallback(() => {
    // A meaningfully-engaged chat that the customer is closing = the natural
    // "conversation resolved" moment to ask for a rating (no-op when the
    // operator hasn't enabled CSAT). Raise it BEFORE we clear the thread so
    // messages.length + the conversation id are still readable.
    maybeRaiseCsat()
    setActive(false)
    setPreview(false)
    setMenuOpen(false)
    setPendingChipText(null)
    if (proactiveText !== null && messages.length === 0) {
      dismissProactive()
      reportProactiveOutcome("dismissed")
    }
    setProactiveText(null)
    try {
      sessionStorage.removeItem("pearl-was-open")
    } catch {
      /* ignore */
    }
    clearPersisted()
  }, [proactiveText, messages.length, dismissProactive, clearPersisted, reportProactiveOutcome, maybeRaiseCsat])

  // External triggers: #pearl hash, [data-pearl-open] clicks, window.Pearl.
  useOpenTriggers({ onOpen: open, onClose: close })

  // Open-state continuity across same-tab navigation: a customer who had the
  // panel open gets the light preview-chip strip on the next page — NEVER the
  // full panel (auto-opening it on every navigation was the #1 annoyance,
  // Justin 2026-06-10). Dismissing the strip suppresses even that.
  useEffect(() => {
    try {
      if (
        hadPersistedHistory &&
        sessionStorage.getItem("pearl-was-open") === "1" &&
        !stripDismissedHere() &&
        // Keep the homepage clean — no auto strip on the site root (2026-06-28).
        !isHomepagePath(pageContext.currentPage)
      ) {
        setPreview(true)
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Belt-and-suspenders homepage cleanliness (2026-06-28): if any path leaves a
  // proactive preview strip showing while the customer is on the site root —
  // e.g. it was raised on a product page, then they navigated home (SPA nav, no
  // widget remount) — clear it. The launcher stays; tapping it still opens
  // Pearl. `active` (the full open panel) is never touched, so a conversation
  // the customer is actually in is left alone.
  useEffect(() => {
    if (preview && !active && isHomepagePath(pageContext.currentPage)) {
      setPreview(false)
    }
  }, [preview, active, pageContext.currentPage])
  useEffect(() => {
    if (active && messages.length > 0) {
      try {
        sessionStorage.setItem("pearl-was-open", "1")
      } catch {
        /* ignore */
      }
    }
  }, [active, messages.length])

  // Referentially-stable handlers for the memoized FinMessage rows — inline
  // lambdas here would defeat memo() and put us back to re-rendering every
  // bubble per keystroke.
  const addToShopifyCartRef = useRef(addToShopifyCart)
  addToShopifyCartRef.current = addToShopifyCart
  const handleMessageAddToCart = useCallback(
    (variantId: number) => void addToShopifyCartRef.current(variantId),
    [],
  )
  const sendTextRef = useRef<(t: string) => void>(() => {})
  const handleMessageSubmitText = useCallback(
    (t: string) => sendTextRef.current(t),
    [],
  )
  const handleMessageSubmitEmail = useCallback(
    (email: string) => sendTextRef.current(`my email is ${email}`),
    [],
  )

  const sendText = useCallback(
    (text: string, attachments: OutgoingAttachment[] = []) => {
      const t = text.trim()
      if (!t && attachments.length === 0) return
      const wasProactive = proactiveText !== null && messages.length === 0
      // "Stop / leave me alone" → permanently suppress the proactive opener for
      // this session so the customer is never popped at again (caught 2026-06-06:
      // rick@one.net begged "stop popping up" and kept getting interrupted).
      // Phase 4: also persist it server-side so it survives across sessions.
      if (t && STOP_INTENT_RE.test(t)) {
        dismissProactive()
        setProactiveText(null)
        reportProactiveOutcome("opted_out")
      } else if (wasProactive) {
        // Engaging with a nudge (chip tap / typing) is the positive signal.
        reportProactiveOutcome("engaged")
      }
      if (!active) setActive(true)
      // Transitioning into full open — preview's job is done.
      setPreview(false)
      // The user is acting on the latest turn — pin to bottom so their
      // message and Pearl's reply are visible even if they'd scrolled up.
      atBottomRef.current = true
      setShowScrollBtn(false)
      send(t, attachments)
    },
    [active, send, dismissProactive, proactiveText, messages.length, reportProactiveOutcome],
  )
  sendTextRef.current = sendText

  /** "Text us" handoff (header, mobile): mint a single-use token for THIS
   *  conversation and deep-link into iMessage/WhatsApp with the
   *  [DYC-CHAT:token] message prefilled. The messaging webhook consumes the
   *  token on the first inbound and links the threads, so the customer
   *  continues the same conversation from their messages app. */
  const openMessagingHandoff = useCallback(
    async (channel: "imessage" | "whatsapp") => {
      const convId = currentConversationId.current
      if (!convId || textUsBusy) return
      setTextUsBusy(channel)
      try {
        const r = await fetch(
          `${props.mountOptions.apiBaseUrl}/api/chat/messaging-links`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sessionToken: props.sessionToken,
              conversationId: convId,
            }),
          },
        )
        if (!r.ok) {
          console.warn("[dyc-pearl] messaging-links failed", r.status)
          return
        }
        const d = (await r.json()) as {
          whatsappUrl?: string | null
          imessageUrl?: string | null
        }
        const url = channel === "imessage" ? d.imessageUrl : d.whatsappUrl
        if (url) {
          setTextUsOpen(false)
          if (channel === "whatsapp") {
            // wa.me resolves on every platform (app or WhatsApp Web) — open
            // in a new tab so a failed deep link never strands the shop tab.
            window.open(url, "_blank", "noopener")
          } else {
            // sms: has no web fallback; same-tab is the only way it opens.
            window.location.href = url
          }
        }
      } catch (err) {
        console.warn("[dyc-pearl] messaging handoff failed", err)
      } finally {
        setTextUsBusy(null)
      }
    },
    [
      currentConversationId,
      textUsBusy,
      props.sessionToken,
      props.mountOptions.apiBaseUrl,
    ],
  )

  /** Dismiss the proactive "Prefer to text?" card for the rest of the session. */
  const dismissHandoffCard = useCallback(() => {
    setHandoffCardDismissed(true)
    try {
      sessionStorage.setItem("pearl-handoff-dismissed", "1")
    } catch {
      /* private mode / storage disabled — fine, it just won't persist */
    }
  }, [])

  function submitDraft() {
    const t = draft.trim()
    if (!t && pendingImages.length === 0) {
      // Empty submit just opens the panel (closed-state launcher behavior).
      if (!active) open()
      return
    }
    // Collapse the attach zone on send — the user shouldn't have to close
    // the attach UI manually before being able to review the message thread.
    setAttachMenuOpen(false)
    sendText(t, pendingImages)
    setDraft("")
    setPendingImages([])
    setUploadError(null)
    if (inputRef.current) {
      inputRef.current.style.height = "auto"
    }
  }

  // Insert an emoji at the textarea cursor (or end-of-text if not focused).
  // Keeps the textarea focused after insert so the customer can keep typing
  // — the picker stays open for multi-emoji bursts (one-emoji-at-a-time
  // felt clunky in QA: people send "👍🔥" not "👍" then "🔥").
  function insertEmojiAtCursor(emoji: string) {
    const ta = inputRef.current
    if (!ta) {
      setDraft((d) => d + emoji)
      return
    }
    const start = ta.selectionStart ?? draft.length
    const end = ta.selectionEnd ?? draft.length
    const next = draft.slice(0, start) + emoji + draft.slice(end)
    setDraft(next)
    // Restore cursor position after the inserted emoji on the next tick
    // (React re-renders the textarea value before we can set selection).
    requestAnimationFrame(() => {
      if (!inputRef.current) return
      const pos = start + emoji.length
      inputRef.current.focus()
      inputRef.current.setSelectionRange(pos, pos)
    })
  }

  // ── Attachments ──────────────────────────────────────────────────────
  // The launcher row can morph into a drop-zone (see attachOpen below).
  // Three file-type lanes: image / video / document. Images get the
  // client-side resize → vision pipeline; videos and PDFs upload raw.
  // accept= on the hidden file input is updated per-lane so the OS
  // picker shows the right shortlist.

  // What kind of upload the customer just initiated. Used to set the
  // file-picker accept= and pick the right post-upload pipeline.
  const [attachAccept, setAttachAccept] = useState<
    "image/*" | "video/*" | "application/pdf" | "image/*,video/*,application/pdf"
  >("image/*,video/*,application/pdf")
  // Drag-over visual state — fires when the customer drags a file over
  // the morphed launcher. Resets on dragleave / drop.
  const [draggingFile, setDraggingFile] = useState(false)

  const pickFile = useCallback(
    (accept: typeof attachAccept = "image/*,video/*,application/pdf") => {
      if (pendingImages.length >= MAX_IMAGES) return
      setAttachAccept(accept)
      // Trigger the input on the next tick so React commits the new
      // accept attribute before the OS dialog reads it.
      setTimeout(() => fileRef.current?.click(), 0)
    },
    [pendingImages.length],
  )

  const removeImage = useCallback((idx: number) => {
    setPendingImages((prev) => prev.filter((_, i) => i !== idx))
  }, [])

  // Common upload path. Images get the canvas resize → JPEG conversion
  // (matches the existing vision pipeline); non-images upload raw so we
  // don't corrupt the bytes.
  const uploadOne = useCallback(
    async (file: File) => {
      if (!active) setActive(true)
      setUploadError(null)
      setUploading(true)
      try {
        const isImage = file.type.startsWith("image/")
        let blob: Blob = file
        let filename = file.name || "upload"
        if (isImage) {
          blob = await resizeImageToJpeg(file)
          filename = "upload.jpg"
        }
        const fd = new FormData()
        fd.append("sessionToken", props.sessionToken)
        fd.append("file", blob, filename)
        const r = await fetch(
          `${props.mountOptions.apiBaseUrl}/api/chat/upload`,
          { method: "POST", body: fd },
        )
        if (!r.ok) {
          // 415 = unsupported type → friendly message vs raw "upload 415"
          if (r.status === 415) {
            setUploadError("That file type isn't supported.")
          } else if (r.status === 413) {
            setUploadError("File's too big — keep it under 10 MB.")
          } else {
            throw new Error(`upload ${r.status}`)
          }
          return
        }
        const data = (await r.json()) as OutgoingAttachment
        if (!data?.url) throw new Error("no url")
        setPendingImages((prev) => [...prev, data].slice(0, MAX_IMAGES))
      } catch {
        setUploadError("Couldn't add that — try a different file.")
      } finally {
        setUploading(false)
        setTimeout(() => inputRef.current?.focus(), 0)
      }
    },
    [active, props.sessionToken, props.mountOptions.apiBaseUrl],
  )

  const onFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = "" // allow re-picking the same file
      if (!file) return
      await uploadOne(file)
    },
    [uploadOne],
  )

  // Drag-and-drop handlers — applied to the morphed launcher when
  // attachMenuOpen is true. Browsers fire `dragenter` on every child
  // crossed, so we count enters/leaves to debounce the visual toggle.
  const dragCounterRef = useRef(0)
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    dragCounterRef.current++
    if (e.dataTransfer.types.includes("Files")) setDraggingFile(true)
  }, [])
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1)
    if (dragCounterRef.current === 0) setDraggingFile(false)
  }, [])
  const handleDragOver = useCallback((e: React.DragEvent) => {
    // Required to enable drop on this element.
    e.preventDefault()
  }, [])
  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      dragCounterRef.current = 0
      setDraggingFile(false)
      const file = e.dataTransfer.files?.[0]
      if (file) await uploadOne(file)
    },
    [uploadOne],
  )

  function autoResize() {
    const ta = inputRef.current
    if (!ta) return
    ta.style.height = "auto"
    ta.style.height = Math.min(ta.scrollHeight, 100) + "px"
  }

  // ── Header menu: new conversation + reopen past chats ────────────────
  const toggleMenu = useCallback(() => {
    setMenuOpen((wasOpen) => {
      const next = !wasOpen
      if (next) {
        // Refresh the history list each time the menu opens.
        setConvosLoading(true)
        listConversations()
          .then((list) => setConversations(list))
          .finally(() => setConvosLoading(false))
      }
      return next
    })
  }, [listConversations])

  const handleNewConversation = useCallback(() => {
    newConversation()
    setProactiveText(null)
    setMenuOpen(false)
    setDraft("")
    atBottomRef.current = true
    setShowScrollBtn(false)
    setTimeout(() => inputRef.current?.focus(), 80)
  }, [newConversation])

  const handlePickConversation = useCallback(
    (id: string) => {
      setMenuOpen(false)
      setProactiveText(null)
      atBottomRef.current = true
      setShowScrollBtn(false)
      loadConversation(id).then(() => {
        // After hydration the list jumps to the bottom (the latest turn).
        requestAnimationFrame(() => scrollToBottom(false))
      })
    },
    [loadConversation, scrollToBottom],
  )

  const showWelcome = active && messages.length === 0
  // Context-aware welcome: returning (logged-in) customers get a personalized
  // greeting + order-first chips; new customers on a product page get
  // product-specific chips; everyone else (incl. homepage) gets the default
  // routing set. A proactive product opener also implies product context.
  const welcomeFirstName =
    props.initialContext.customerName?.trim().split(/\s+/)[0] || null
  const onProductPage = Boolean(pageContext.productHandle || proactiveText)

  // ── Identity gate ────────────────────────────────────────────────────
  //
  // Hard UI gate that blocks chips + the composer until the customer
  // submits their name + email. Replaces the prompt-only "Pearl asks for
  // name first" flow that had model variance — Pearl sometimes asked,
  // sometimes skipped, sometimes buried the ask at the end of a long
  // technical answer. Deterministic UI removes that variance entirely.
  //
  // Skipped when:
  //   - We already know the customer (initialContext.customerName from
  //     the page-context script tag = logged-in Shopify customer).
  //   - localStorage flag says they captured before (persists across tabs +
  //     navigations, unlike sessionStorage which is per-tab). Customers
  //     who click product cards used to get re-prompted on the product
  //     page because sessionStorage doesn't survive the new pageload.
  //   - The customer has already engaged (messages.length > 0) — we don't
  //     interrupt mid-conversation.
  // `?nogate=1` URL param bypasses the identity capture form — for local
  // testing only. Mirrors the existing `?canvas=1` testing-shortcut pattern.
  // Same one-shot read at mount (state initialization), no need to react to
  // URL changes mid-conversation.
  const bypassIdentityGate =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("nogate") === "1"
  // Union of every signal that means "skip the gate":
  //   - bypassIdentityGate: `?nogate=1` URL param (local testing)
  //   - localStorage flag: persisted capture across browser sessions
  //   - sessionStorage flag: legacy per-tab capture (kept for customers
  //     who captured before we moved to localStorage)
  //   - initialContext.customerEmail: server hydrated identity from a
  //     prior session (fingerprint match) so we don't re-ask returning
  //     visitors who already told Pearl who they are.
  const initialIdentityCaptured =
    bypassIdentityGate ||
    (typeof localStorage !== "undefined" &&
      localStorage.getItem(IDENTITY_CAPTURED_KEY) === "1") ||
    (typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(IDENTITY_CAPTURED_KEY) === "1") ||
    Boolean(props.initialContext.customerEmail)
  const [identityCaptured, setIdentityCaptured] = useState(
    Boolean(welcomeFirstName) || initialIdentityCaptured,
  )
  const [identitySubmitting, setIdentitySubmitting] = useState(false)
  // Inline error shown under the identity form when the SERVER rejects the
  // email (bad format / disposable domain / junk). The form also runs a
  // lighter client-side format check for instant feedback; this carries the
  // server's verdict, which is the source of truth.
  const [identityError, setIdentityError] = useState<string | null>(null)
  // Identity-gate TIMING is A/B-tested (identity_gate_timing experiment,
  // server-assigned, stable per session). Two arms:
  //   - "chat_start" (control / default): gate BEFORE the first message.
  //   - "after_first_value": let the first question + Pearl's reply through,
  //     THEN gate before the customer can send again — tests whether proving
  //     value first lifts capture rate without inflating bounce. We wait for
  //     `!pending` so the gate appears only once Pearl's first reply lands
  //     (messages.length >= 2 = customer turn + Pearl turn), not mid-stream.
  // Unknown / missing variant → control behavior. The server mirrors this
  // arm in the system prompt (see identity_gate_timing override) so Pearl's
  // conversational onboarding matches the hard-gate timing.
  const gateTiming = props.experiments?.identity_gate_timing
  const gateReady =
    gateTiming === "after_first_value"
      ? messages.length >= 2 && !pending
      : messages.length === 0
  // A proactive-chip tap (valueFirstEntry) suppresses the widget identity form
  // ENTIRELY — answering a useful question must never be walled behind a
  // name/email form (Justin 2026-06-15). Pearl answers first, then captures
  // name/email CONVERSATIONALLY (prompt MODE A) when the customer goes deeper.
  const needsIdentityGate = !identityCaptured && gateReady && !valueFirstEntry
  // Deferred-arm gate renders BELOW the conversation (showWelcome is false
  // once messages exist), so it needs its own flag + intro that reads right
  // after an exchange rather than cold.
  const showDeferredGate = needsIdentityGate && messages.length > 0
  const deferredIdentityIntro =
    "Glad that helped! Before we keep going — what's your name and email? I'll use it to save your project and pick up where we left off."

  // Proactive "Prefer to text?" card — after the first real exchange (a customer
  // message + Pearl's reply), invite them to carry the chat to iMessage/WhatsApp.
  // Dismissible, shown once per session, and never while the identity gate is up
  // or when the customer has muted proactive ("stop means stop"). Renders inline
  // at the foot of the conversation on BOTH desktop and mobile.
  const proactiveMuted =
    typeof sessionStorage !== "undefined" &&
    sessionStorage.getItem("pearl-proactive-dismissed") === "1"
  useEffect(() => {
    if (handoffCardDismissed || needsIdentityGate || proactiveMuted) {
      setHandoffVisible(false)
      return
    }
    if (handoffArmCountRef.current === 0) {
      // Arm once the first exchange has fully landed (a customer message + a
      // settled Pearl reply). >= 2 is robust to extra tool/system rows.
      if (messages.length >= 2 && !pending && currentConversationId.current) {
        handoffArmCountRef.current = messages.length
        setHandoffVisible(true)
      }
    } else if (messages.length > handoffArmCountRef.current) {
      // They kept going — retire the nudge (header bubble remains available).
      setHandoffVisible(false)
    }
  }, [messages.length, pending, handoffCardDismissed, needsIdentityGate, proactiveMuted])
  const showHandoffCard = handoffVisible

  // Preview-chip tap handler. Routes around the gate-bypass bug: instead
  // of sending immediately (which marked messages.length > 0 and dodged
  // the gate), we either:
  //   - capture identity FIRST then send (gate path) — pendingChipText
  //     gets sent inside handleIdentitySubmit after capture succeeds
  //   - send immediately if identity is already on file (fast path)
  const handlePreviewChipTap = useCallback(
    (chipText: string) => {
      // The customer tapped a proactive chip to get useful info — ANSWER IT
      // FIRST. Flip to value-first gating so the name/email form never walls the
      // answer; identity gets captured softly (deferred gate) only if they
      // continue past it. Bad UX to gate a helpful answer behind a form.
      setValueFirstEntry(true)
      sendText(chipText)
    },
    [sendText],
  )
  // Gate intro — product-aware ONLY when we're ACTUALLY on a product page.
  // Previous version used pageContext.productTitle directly, but that pulls
  // from og:title even on non-product pages (widget-preview, blog posts,
  // landing pages) and produced things like "I can help with the Pearl
  // Widget" on the test harness. Gating on productHandle (which only resolves
  // from a /products/* URL match) keeps it honest.
  // Clean product name for the intro line:
  //   - Strip a leading article ("The Anthias..." → "Anthias...").
  //   - Word-boundary truncate at ~50 chars so we never get "...You Can Cou"
  //     style mid-word cuts.
  //   - If the cleaned name is still longer than 50 chars even at the first
  //     word boundary, fall back to the generic intro — a 60+ char product
  //     name in the bubble reads as broken anyway. The customer can see what
  //     they're looking at on the page.
  const identityProductName =
    pageContext.productHandle && pageContext.productTitle
      ? truncateAtWord(pageContext.productTitle.replace(/^(the|a|an)\s+/i, ""), 50)
      : ""
  const identityIntro = identityProductName
    ? `Hi, I'm Pearl. I can help with the ${identityProductName} — first, what's your name and the best email or mobile?`
    : "Hi, I'm Pearl. Before we get started, what's your name and the best email or mobile?"

  const handleIdentitySubmit = useCallback(
    async (name: string, email: string, phone: string) => {
      setIdentitySubmitting(true)
      setIdentityError(null)
      try {
        const r = await fetch(
          `${props.mountOptions.apiBaseUrl}/api/chat/identity`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            // email OR phone — the form requires at least one; send only what
            // was filled so the server validates/persists the right field.
            body: JSON.stringify({
              sessionToken: props.sessionToken,
              name,
              ...(email ? { email } : {}),
              ...(phone ? { phone } : {}),
            }),
          },
        )
        if (!r.ok) {
          // Surface the server's rejection inline so the customer can fix it
          // (esp. the email gate — disposable/junk addresses only fail here).
          // Keep the gate up; identity is NOT captured.
          let msg = "Something went wrong — please try again."
          try {
            const body = (await r.json()) as { error?: string }
            if (body?.error) msg = body.error
          } catch {
            /* non-JSON error body — keep the generic message */
          }
          setIdentityError(msg)
          console.warn("[dyc-pearl] identity capture failed", r.status)
          return
        }
        try {
          // localStorage so identity persists across tabs/navigations.
          // Server-side chat_sessions.customer_email is the durable truth;
          // this client flag just skips re-rendering the gate quickly on
          // subsequent page loads before validateSession resolves.
          localStorage.setItem(IDENTITY_CAPTURED_KEY, "1")
        } catch {
          /* private mode / quota — ignore, server still has the row */
        }
        setIdentityCaptured(true)
        // If the customer arrived here by tapping a floating preview
        // chip, the chip's text was queued behind the gate — send it
        // now that identity is captured. Slight setTimeout so React
        // commits identityCaptured=true before sendText reads state
        // (otherwise needsIdentityGate could still be true in the same
        // tick and downstream branching would misroute).
        if (pendingChipText) {
          const queued = pendingChipText
          setPendingChipText(null)
          setTimeout(() => sendText(queued), 0)
        }
      } finally {
        setIdentitySubmitting(false)
      }
    },
    [
      props.mountOptions.apiBaseUrl,
      props.sessionToken,
      pendingChipText,
      sendText,
    ],
  )

  // A/B-aware welcome text. Variant assignment comes from server
  // (deterministic by session_id) so the same visitor always sees the
  // same variant. Unknown variant key → fall back to control.
  const welcomeVariant =
    (props.experiments?.welcome_message &&
      WELCOME_VARIANTS[props.experiments.welcome_message]) ||
    WELCOME_TEXT
  const welcomeText = welcomeFirstName
    ? greetReturning(welcomeFirstName)
    : (proactiveText ?? welcomeVariant)
  // Returning customer chips win first — returning customers usually came
  // back for an order, not for product browsing, so even with a proactive
  // product opener firing, the order-first chips are more useful.
  // Otherwise: curated chips (pearl_product_openers.chips) > per-product-class
  // URL-pattern chips (chipsForProduct) > generic homepage WELCOME_CHIPS.
  // SUPPRESSED entirely while the identity gate is up — the user explicitly
  // doesn't want chips or other distractions before identity is captured.
  const welcomeChips = needsIdentityGate
    ? []
    : welcomeFirstName
      ? RETURNING_CHIPS
      : curatedChips && curatedChips.length >= 2
        ? curatedChips
        : onProductPage
          ? chipsForProduct(pageContext.productHandle)
          : WELCOME_CHIPS
  // Preview-state chip set — same precedence chain but ignores the
  // identity gate. Preview is the PRE-engagement surface (floating chips
  // outside the panel); the identity-form only renders INSIDE the panel,
  // so suppressing chips here on `needsIdentityGate` would just show an
  // empty preview-strip. Tapping a chip in preview transitions into the
  // panel where the gate will show normally on first send.
  const previewChips = welcomeFirstName
    ? RETURNING_CHIPS
    : curatedChips && curatedChips.length >= 2
      ? curatedChips
      : onProductPage
        ? chipsForProduct(pageContext.productHandle)
        : WELCOME_CHIPS
  // The suggestion stack is showing whenever proactive content has fired and
  // we have at least two chips — kept mounted through the closing animation so
  // the reverse-morph can play out before teardown.
  const showStack = preview && !active && !drawerOpen && previewChips.length > 0

  // Graceful dismiss: play the reverse-morph (chips retract into the chip, the
  // chip glides back to center via the same transition), THEN clear state. The
  // chip itself never unmounts — only the suggestions do — so the orb stays put.
  const dismissStack = useCallback(() => {
    if (stackCloseTimer.current !== null) return
    setStackClosing(true)
    stackCloseTimer.current = window.setTimeout(() => {
      // PER-PRODUCT dismiss (the X reads as "dismiss this pitch", not a
      // store-wide mute — audit 2026-06-11). Session-wide kill stays for
      // stop-intent and non-product pages.
      try {
        const handle = pageContext.productHandle
        if (handle) {
          sessionStorage.setItem("pearl-strip-dismissed:" + handle, "1")
        } else {
          dismissProactive()
        }
        sessionStorage.removeItem("pearl-was-open")
      } catch {
        /* ignore */
      }
      setProactiveText(null)
      setCuratedChips(null)
      setPreview(false)
      setStackClosing(false)
      stackCloseTimer.current = null
    }, 240)
  }, [pageContext.productHandle, dismissProactive])

  useEffect(
    () => () => {
      if (stackCloseTimer.current !== null)
        window.clearTimeout(stackCloseTimer.current)
    },
    [],
  )
  const canSend = draft.trim().length > 0 || pendingImages.length > 0
  const hasMedia = pendingImages.length > 0 || uploading
  // Animate the orb avatar unless the visitor prefers reduced motion.
  const orbAnimated =
    typeof window === "undefined" ||
    !window.matchMedia ||
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches

  return (
    <CartContext.Provider value={{ addToCart: addToShopifyCart }}>
    <div
      className={cn(
        fm.root,
        active && fm.open,
        // Preview state: input pill visible + chips strip above, panel
        // stays closed. Hidden once `active` is set (open wins).
        preview && !active && fm.preview,
        pending && fm.thinking,
        pageContext.productHandle && fm["on-product"],
      )}
      // Hide the ENTIRE widget — launcher, preview strip, AND the open panel /
      // canvas / mobile sheet — while a storefront drawer or the mobile menu
      // covers the page (per request 2026-06-28). display:none cascades to the
      // fixed-position descendants (BottomSheet, launcher) too, so nothing peeks
      // over the overlay. No unmount — state is preserved and the widget
      // reappears unchanged when the drawer/menu closes.
      style={drawerOpen ? { display: "none" } : undefined}
    >
      {/* Backdrop blur is set inline (not via CSS) because the build's CSS
        * minifier strips the standard `backdrop-filter` and keeps only the
        * `-webkit-` form, so the blur rendered on iOS/Safari but not reliably
        * on desktop. Inline styles bypass minification and carry both forms,
        * so the mobile-homepage blur renders identically on every page/browser. */}
      <div
        className={fm.scrim}
        style={{
          backdropFilter: active ? "blur(6px)" : "blur(0px)",
          WebkitBackdropFilter: active ? "blur(6px)" : "blur(0px)",
        }}
      />

      {/* Mini-cart Phase 1: when the canvas feature flag is on AND the
        * canvas has been opened (by a basket_update or other rich-card
        * action), the panel shrinks to ~40% on desktop and the CanvasShell
        * occupies the right ~60%. Mobile falls back to single-column. */}
      <div
        className={cn(
          fm.layout,
          props.canvasEnabled && canvas.isOpen && fm["layout-with-canvas"],
          props.canvasEnabled && canvas.isOpen && canvasClosing && fm["layout-closing"],
        )}
        // Inline backdrop-filter (canvas-mode only) — Vite's CSS optimizer
        // strips the unprefixed declaration from .layout-with-canvas, same
        // issue we hit on .canvas earlier. Inline guarantees both forms ship
        // so the unified-container blur actually applies in Chromium.
        //
        // CRITICAL: omit on mobile. backdrop-filter creates a CSS containing
        // block, so any `position: fixed` descendant (BottomSheet!) anchors
        // to THIS div instead of the viewport. On mobile the layout-with-
        // canvas wrapper goes `position: static` (handled in CSS) so its
        // computed height is 0, and the BottomSheet ended up positioned
        // at top:-511px — entirely above the viewport. Caught 2026-06-03
        // when mobile canvas appeared empty. Desktop still gets the blur
        // since the layout has its own glass treatment there.
        style={
          props.canvasEnabled && canvas.isOpen && !isMobile
            ? {
                backdropFilter: "blur(32px) saturate(150%)",
                WebkitBackdropFilter: "blur(32px) saturate(150%)",
              }
            : undefined
        }
      >
      {/* Dark-glass conversation container — header + messages only. Floats
        * ABOVE the input bar (which is a separate pill below it). */}
      <div
        className={cn(
          fm.panel,
          props.canvasEnabled && canvas.isOpen && fm.panelWithCanvas,
        )}
        ref={panelRef}
        role="dialog"
        aria-label="Pearl chat"
        // The closed panel is hidden visually by clip-path, which leaves its
        // buttons (close, menus) in the accessibility tree AND tabbable.
        // `inert` removes it from both focus order and the a11y tree while
        // closed (React 19 supports the boolean prop); aria-hidden mirrors
        // it for good measure.
        inert={!active ? true : undefined}
        aria-hidden={!active}
        // Inline blur ONLY in single-panel mode. When the canvas is open,
        // the unified container (.layout-with-canvas) owns the glass
        // treatment and the panel is a flat content pane — adding a blur
        // here would double up the filter and visibly grey out the pane.
        style={
          props.canvasEnabled && canvas.isOpen
            ? undefined
            : {
                backdropFilter: "blur(32px) saturate(150%)",
                WebkitBackdropFilter: "blur(32px) saturate(150%)",
              }
        }
      >
        <header className={fm.header}>
          <span className={fm["header-avatar"]} aria-hidden="true">
            <PearlOrb id="po-hdr" animated={orbAnimated} />
          </span>
          <div className={fm["header-id"]}>
            <span className={fm["header-name"]}>Pearl</span>
            <span className={fm["header-beta"]}>Beta</span>
            <button
              type="button"
              ref={helpBtnRef}
              className={cn(fm["header-help"], helpOpen && fm["header-help-open"])}
              onClick={() => setHelpOpen((v) => !v)}
              aria-label="About Pearl"
              aria-expanded={helpOpen}
              title="About Pearl"
            >
              <HelpCircleIcon />
            </button>
          </div>
          {/* "Text us" — channel handoff (desktop + mobile). Lets the customer
            * carry this conversation to iMessage or WhatsApp (minted token deep
            * link) so they can keep shopping from their messages app instead
            * of staying on the site. Only once a conversation exists — the
            * token is minted against the conversation id. (Un-gated from
            * mobile-only 2026-06-16 — Justin wants it on both.) */}
          {messages.length > 0 && (
            <button
              type="button"
              ref={textUsBtnRef}
              className={cn(fm["header-menu"], textUsOpen && fm["header-menu-open"])}
              onClick={() => setTextUsOpen((v) => !v)}
              aria-label="Continue by text message"
              aria-expanded={textUsOpen}
              title="Continue by text"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-9 8.36 8.5 8.5 0 0 1-3.8-.9L3 20l1.04-4.2A8.38 8.38 0 1 1 21 11.5z" />
              </svg>
            </button>
          )}
          {/* Canvas toggle — shows whenever the canvas surface has anything
            * worth surfacing (active payload OR basket items OR history).
            * Behavior differs by viewport:
            *   - Mobile: toggles the BottomSheet peek ↔ half
            *   - Desktop: toggles the side canvas pane open ↔ closed
            *     (added 2026-06-03 — without it, once Justin closed the
            *     canvas via the canvas-header X there was no way to reopen
            *     it short of triggering a new tool call.)
            * Hidden entirely when canvas has nothing in it. */}
          {props.canvasEnabled && (canvas.activePayload || canvas.history.length > 0 || (canvas.basket?.items.length ?? 0) > 0) && (
            <button
              type="button"
              className={fm["header-menu"]}
              onClick={() => {
                if (isMobile) {
                  const sheet = bottomSheetRef.current
                  if (!sheet) return
                  sheet.expand(sheet.state === "peek" ? "half" : "peek")
                } else {
                  // Desktop: flip canvas.isOpen. Close via requestCanvasClose
                  // so the reverse-morph plays before the CanvasShell unmounts
                  // (a bare closeCanvas() snaps it shut — see canvasClosing).
                  if (canvas.isOpen) requestCanvasClose()
                  else openCanvas()
                }
              }}
              aria-label="Toggle Pearl canvas"
              aria-pressed={canvas.isOpen}
              title="Pearl canvas"
            >
              <CanvasPanelIcon />
            </button>
          )}
          <button
            type="button"
            ref={menuBtnRef}
            className={cn(fm["header-menu"], menuOpen && fm["header-menu-open"])}
            onClick={toggleMenu}
            aria-label="Conversations menu"
            aria-expanded={menuOpen}
          >
            <ChevronDown />
          </button>
          {/* Close X lives on the chat-header in single-panel mode. When
            * the canvas is open the header reads as one unified bar
            * (chat label left, canvas label right) — so the close X
            * moves to the right end of the canvas-header instead, and
            * THIS one hides to avoid two close buttons on one bar. */}
          {!(props.canvasEnabled && canvas.isOpen) && (
            <button
              type="button"
              className={fm["header-close"]}
              onClick={close}
              aria-label="Close chat"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M6 18L18 6" />
              </svg>
            </button>
          )}
        </header>
        {textUsOpen && (
          <div className={fm["textus-menu"]} role="menu" aria-label="Continue by text">
            <div className={fm["textus-title"]}>
              Take this chat with you — we'll text from our DipYourCar line.
            </div>
            <button
              type="button"
              role="menuitem"
              className={fm["textus-item"]}
              disabled={textUsBusy !== null}
              onClick={() => void openMessagingHandoff("imessage")}
            >
              {textUsBusy === "imessage" ? "Opening…" : "Continue on iMessage"}
            </button>
            <button
              type="button"
              role="menuitem"
              className={fm["textus-item"]}
              disabled={textUsBusy !== null}
              onClick={() => void openMessagingHandoff("whatsapp")}
            >
              {textUsBusy === "whatsapp" ? "Opening…" : "Continue on WhatsApp"}
            </button>
          </div>
        )}

        {/* Conversations menu — new chat + reopen a past thread. */}
        {menuOpen && (
          <>
            <div
              className={fm["menu-backdrop"]}
              onClick={() => setMenuOpen(false)}
            />
            <div className={fm.menu} role="menu">
              <button
                type="button"
                className={fm["menu-new"]}
                onClick={handleNewConversation}
                role="menuitem"
              >
                <span className={fm["menu-new-icon"]} aria-hidden="true">
                  <PlusIcon />
                </span>
                New conversation
              </button>

              {(convosLoading || conversations.length > 0) && (
                <div className={fm["menu-label"]}>Recent</div>
              )}
              {convosLoading && (
                <div className={fm["menu-empty"]}>Loading…</div>
              )}
              {!convosLoading &&
                conversations.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={cn(
                      fm["menu-convo"],
                      c.id === currentConversationId.current &&
                        fm["menu-convo-active"],
                    )}
                    onClick={() => handlePickConversation(c.id)}
                    role="menuitem"
                  >
                    <span className={fm["menu-convo-icon"]} aria-hidden="true">
                      <ChatIcon />
                    </span>
                    <span className={fm["menu-convo-text"]}>
                      <span className={fm["menu-convo-preview"]}>
                        {c.preview}
                      </span>
                      <span className={fm["menu-convo-time"]}>
                        {timeAgo(c.updatedAt)}
                      </span>
                    </span>
                  </button>
                ))}
            </div>
          </>
        )}

        {/* About-Pearl sheet — slides in from the header, dismisses on backdrop
          * tap, Escape, or "Got it". Lives next to the conversations menu so
          * both share the same overlay z-index stack. */}
        {helpOpen && (
          <>
            <div
              className={fm["menu-backdrop"]}
              onClick={() => setHelpOpen(false)}
            />
            <div
              className={fm["help-sheet"]}
              role="dialog"
              aria-labelledby="pearl-help-title"
            >
              <div className={fm["help-title"]} id="pearl-help-title">
                Pearl is in training
              </div>
              <p className={fm["help-lead"]}>
                Pearl is our AI helper, still learning the DipYourCar product
                line. She&apos;s excellent at answering your questions, finding
                factory paint codes, sizing kits, and much more.
              </p>

              <p className={fm["help-lead"]}>
                However like any AI, Pearl isn&apos;t perfect and may
                occasionally make mistakes. Please keep this in mind when
                talking to her about critical items as some questions still may
                be more appropriate for a live team member.
              </p>

              <div className={fm["help-section-label"]}>Want a real person?</div>
              <p className={fm["help-lead"]}>
                Just say <em>&ldquo;chat with the DYC team&rdquo;</em> anytime
                and we&apos;ll jump in. We&apos;re here weekdays 9am–5pm EST
                and step in as fast as we can.
              </p>

              <p className={fm["help-foot"]}>
                Pearl learns from every conversation. Thanks for helping us make
                her sharper.
              </p>

              <button
                type="button"
                className={fm["help-close"]}
                onClick={() => setHelpOpen(false)}
              >
                Got it
              </button>
            </div>
          </>
        )}

        {/* role="log" + polite live region: screen readers announce Pearl's
          * replies as they stream in, without yanking focus. aria-atomic
          * false so only the appended content is announced, not the whole
          * thread on every token. Lives on the LIST container (not per
          * message) to avoid double-announcing. */}
        <div
          className={fm.body}
          ref={bodyRef}
          onScroll={handleBodyScroll}
          role="log"
          aria-live="polite"
          aria-atomic="false"
        >
          {showWelcome && (
            <>
              <div className={fm.intro}>
                {needsIdentityGate ? identityIntro : welcomeText}
              </div>
              {needsIdentityGate ? (
                /*
                 * Hard gate: form replaces chips entirely until submitted.
                 * The form's onSubmit fires /api/chat/identity, flips
                 * identityCaptured to true, and the next render shows the
                 * normal chips + enables the composer.
                 */
                <IdentityCaptureForm
                  onSubmit={handleIdentitySubmit}
                  pending={identitySubmitting}
                  serverError={identityError}
                />
              ) : (
                <Chips chips={welcomeChips} onPick={sendText} />
              )}
            </>
          )}

          {messages.map((m) => (
            <FinMessage
              key={m.id}
              message={m}
              onSubmitText={handleMessageSubmitText}
              onSubmitEmail={handleMessageSubmitEmail}
              onAddToShopifyCart={handleMessageAddToCart}
            />
          ))}

          {/* Deferred identity gate (identity_gate_timing="after_first_value").
            * Once Pearl has proven value with her first reply, the form
            * renders below the conversation and the composer disables
            * (same `needsIdentityGate` wiring as the cold gate) until the
            * customer submits. Capture flips identityCaptured → form clears,
            * composer re-enables, any queued chip text flushes. */}
          {showDeferredGate && (
            <>
              <div className={fm.intro}>{deferredIdentityIntro}</div>
              <IdentityCaptureForm
                onSubmit={handleIdentitySubmit}
                pending={identitySubmitting}
                serverError={identityError}
              />
            </>
          )}

          {/* Proactive channel handoff — "Prefer to text?" Mints a token on tap
            * and deep-links into iMessage/WhatsApp so the customer keeps THIS
            * conversation from their phone. Same card the agent console pushes,
            * surfaced proactively after the first exchange (both platforms). */}
          {showHandoffCard && (
            <div className={cn(fm["handoff-card"], fm["handoff-card-proactive"])}>
              <p className={fm["handoff-title"]}>
                Prefer to text? Pick this up on your phone.
              </p>
              <div className={fm["handoff-actions"]}>
                <button
                  type="button"
                  className={cn(fm["handoff-btn"], fm["handoff-btn-imessage"])}
                  disabled={textUsBusy === "imessage"}
                  onClick={() => {
                    void openMessagingHandoff("imessage")
                    dismissHandoffCard()
                  }}
                >
                  {textUsBusy === "imessage" ? "Opening…" : "Continue on iMessage"}
                </button>
                <button
                  type="button"
                  className={cn(fm["handoff-btn"], fm["handoff-btn-whatsapp"])}
                  disabled={textUsBusy === "whatsapp"}
                  onClick={() => {
                    void openMessagingHandoff("whatsapp")
                    dismissHandoffCard()
                  }}
                >
                  {textUsBusy === "whatsapp" ? "Opening…" : "Continue on WhatsApp"}
                </button>
              </div>
              <button
                type="button"
                className={fm["handoff-dismiss"]}
                onClick={dismissHandoffCard}
              >
                Not now
              </button>
            </div>
          )}

          <div className={fm.typing} aria-live="polite">
            <i />
            <i />
            <i />
          </div>
        </div>

        {/* Scroll-to-bottom — appears only when scrolled up off the latest. */}
        <button
          type="button"
          className={cn(
            fm["scroll-bottom"],
            showScrollBtn && fm["scroll-bottom-show"],
          )}
          onClick={() => scrollToBottom(true)}
          aria-label="Scroll to latest"
          tabIndex={showScrollBtn ? 0 : -1}
        >
          <ChevronDown />
        </button>
      </div>

      {/* Pearl mini-cart canvas — feature-flagged side panel on desktop,
        * bottom sheet on mobile. Mobile is always rendered (peek state) while
        * the feature flag is on, since the sheet itself contains the
        * persistent basket chip; desktop only mounts when `isOpen` (a
        * basket_update or other canvas-routed action fires). The two
        * branches share the same canvas state and reply handlers. */}
      {props.canvasEnabled && canvas.isOpen && (
        isMobile ? (
          <BottomSheet
            ref={bottomSheetRef}
            peekContent={
              <BasketDockChip
                basket={canvas.basket}
                onClick={() => bottomSheetRef.current?.expand("half")}
              />
            }
          >
            {/* Sheet body — renders the active payload above the history
              * strip, same as the desktop canvas. If nothing is staged
              * (no basket yet, no payloads), we show a muted hint that
              * matches CanvasShell's empty state for consistency. */}
            {canvas.activePayload ? (
              <CanvasPayloadRouter
                payload={canvas.activePayload}
                basket={canvas.basket}
                onUserReply={(text) => send(text)}
                onAddToCart={(kitVariantId, addOnNames) => {
                  // Kit → straight into the Shopify cart + open the cart
                  // drawer (no hidden chat-cart / "ready to checkout" step).
                  void addToShopifyCart(kitVariantId)
                  // Add-ons are product ids needing variant resolution, so
                  // hand them to Pearl to add.
                  if (addOnNames.length > 0)
                    send(`Also add ${addOnNames.join(" + ")} to my cart`)
                }}
                onRemoveItem={(itemId) => updateBasketSilent("remove", itemId)}
                onQtyChange={(itemId, qty) => updateBasketSilent("qty", itemId, qty)}
                onCheckout={() => {
                  // Items are already in the live cart (adds mirror at add
                  // time) — commit just clears the canvas mirror; then show
                  // the customer their REAL cart.
                  void commitBasketSilent()
                  window.location.href = "/cart"
                }}
              />
            ) : (
              <div className={fm["canvas-empty"]}>
                <span>Nothing here yet — Pearl will surface results.</span>
                <button
                  type="button"
                  className={fm["canvas-empty-back"]}
                  onClick={() => bottomSheetRef.current?.expand("peek")}
                >
                  Back to chat
                </button>
              </div>
            )}
            <HistoryStrip
              payloads={canvas.history}
              activeId={canvas.activePayload?.id ?? null}
              onSelect={setActivePayload}
            />
          </BottomSheet>
        ) : (
          <CanvasShell
            canvas={canvas}
            onUserReply={(text) => send(text)}
            onAddToCart={(kitVariantId, addOnNames) => {
              // Kit → straight into the Shopify cart + open the cart drawer
              // (no hidden chat-cart / "ready to checkout" step). Mirrors the
              // mobile BottomSheet branch above. Add-ons (product ids) go via
              // Pearl to resolve their variant.
              void addToShopifyCart(kitVariantId)
              if (addOnNames.length > 0)
                send(`Also add ${addOnNames.join(" + ")} to my cart`)
            }}
            // Use the silent basket API (no chat-message round-trip) for
            // qty + remove edits — mirrors the mobile BottomSheet branch.
            // Previously these sent "Remove item <uuid> from basket" /
            // "Set item <uuid> quantity to N" into chat, which (a) leaked
            // internal item IDs into the customer-facing thread and
            // (b) made the customer wait a full Pearl turn for a UI op
            // they expected to feel instant. Caught 2026-06-03 alongside
            // the basket→cart rename.
            onRemoveItem={(itemId) => updateBasketSilent("remove", itemId)}
            onQtyChange={(itemId, qty) => updateBasketSilent("qty", itemId, qty)}
            onCheckout={() => {
                  // Items are already in the live cart (adds mirror at add
                  // time) — commit just clears the canvas mirror; then show
                  // the customer their REAL cart.
                  void commitBasketSilent()
                  window.location.href = "/cart"
                }}
            onSelectPayload={setActivePayload}
            // When canvas is open the unified header bar shows ONE close X
            // (on the canvas-header right side). Pressing it closes the
            // whole widget — same as the chat-header X did in single-panel
            // mode. `closeCanvas` is called too so when the user reopens
            // the chat later, it starts in chat-only mode.
            onClose={() => {
              closeCanvas()
              close()
            }}
          />
        )
      )}
      </div>

      {/* Collapsed launcher + proactive stack — ONE persistent container so
        * the closed→preview change is a continuous morph, not a swap. The
        * brand chip lives at the bottom and self-centers when it's alone;
        * when a proactive nudge fires, the chip glides LEFT (--chip-x → 0)
        * while the suggestion chips grow upward out of it (fm-chip-grow,
        * staggered from the chip outward). Tapping the chip opens the full
        * composer (it doubles as the free-text "ask anything" affordance, so
        * there's no separate opener pill). The × plays a reverse-morph
        * (dismissStack) — chips retract into the chip, chip re-centers — then
        * tears down. Hidden when the panel is open or a storefront drawer
        * (cart / nav-finder) is showing. */}
      {!drawerOpen && !active && (
        <div
          className={cn(
            fm["launcher-stack"],
            showStack && fm["launcher-stack-open"],
            stackClosing && fm["launcher-stack-closing"],
          )}
          role="region"
          aria-label="Pearl"
        >
          {showStack && (
            <>
              <button
                type="button"
                className={fm["preview-dismiss"]}
                onClick={dismissStack}
                aria-label="Dismiss suggestions"
                title="Dismiss"
              >
                <XMark />
              </button>
              {previewChips.map((chip, i) => {
                // k = distance from the chip (0 = chip-adjacent, bottom). Each
                // pill STARTS pushed down toward the chip (--from-y) and slides
                // up into its slot — masked behind the opaque chip until it
                // clears the top edge, so it reads as growing OUT of the chip.
                // Chip-adjacent emerges first; the topmost last.
                const k = previewChips.length - 1 - i
                return (
                  <button
                    key={chip}
                    type="button"
                    className={fm["preview-chip"]}
                    style={
                      {
                        ["--from-y"]: `${(k + 1) * 34}px`,
                        // Emerge AFTER the chip has stretched + snapped into
                        // place (~550ms), chip-adjacent first.
                        animationDelay: `${430 + k * 55}ms`,
                      } as CSSProperties
                    }
                    onClick={() => handlePreviewChipTap(chip)}
                  >
                    {chip}
                  </button>
                )
              })}
            </>
          )}
          <button
            type="button"
            className={fm["chip-launcher"]}
            onClick={open}
            aria-label="Open chat with Pearl"
          >
            {/* Inner wrapper so the chip's squash-and-stretch (scaleX on the
              * button) can be counter-scaled here — the orb + label stay
              * undistorted while the pill stretches leftward. */}
            <span className={fm["chip-inner"]}>
              <span className={fm["chip-avatar"]} aria-hidden="true">
                <PearlOrb id="po-chip" animated={orbAnimated} />
              </span>
              <span className={fm["chip-label"]}>Ask Pearl</span>
            </span>
          </button>
        </div>
      )}

      {/* Post-resolution CSAT prompt — operator-gated (props.csatEnabled).
        * Only mounts when maybeRaiseCsat() set a conversation id, which it
        * does ONLY when CSAT is enabled, the chat had >= 2 messages, and the
        * customer just closed/minimised it. Renders ONCE per conversation.
        * Lives outside the (inert) panel so it stays interactive after the
        * panel collapses to the chip. A 1–5 star row + one optional line. */}
      {csatConvId && (
        <div
          className={fm["csat-card"]}
          role="dialog"
          aria-label="Rate your chat with Pearl"
        >
          <button
            type="button"
            className={fm["csat-dismiss"]}
            onClick={dismissCsat}
            aria-label="Dismiss rating"
            title="No thanks"
          >
            <XMark />
          </button>
          {csatDone ? (
            <div className={fm["csat-thanks"]}>Thanks for the feedback!</div>
          ) : (
            <>
              <div className={fm["csat-title"]}>How was your chat?</div>
              <div
                className={fm["csat-stars"]}
                role="radiogroup"
                aria-label="Rating, 1 to 5 stars"
                onMouseLeave={() => setCsatHover(null)}
              >
                {[1, 2, 3, 4, 5].map((n) => {
                  const filled = (csatHover ?? csatScore ?? 0) >= n
                  return (
                    <button
                      key={n}
                      type="button"
                      className={cn(
                        fm["csat-star"],
                        filled && fm["csat-star-on"],
                      )}
                      role="radio"
                      aria-checked={csatScore === n}
                      aria-label={`${n} star${n === 1 ? "" : "s"}`}
                      disabled={csatSubmitting}
                      onMouseEnter={() => setCsatHover(n)}
                      onFocus={() => setCsatHover(n)}
                      onClick={() => void submitCsat(n)}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 2.5l2.95 5.98 6.6.96-4.78 4.66 1.13 6.57L12 17.6l-5.9 3.1 1.13-6.57L2.45 9.44l6.6-.96L12 2.5z" />
                      </svg>
                    </button>
                  )
                })}
              </div>
              <input
                type="text"
                className={fm["csat-comment"]}
                placeholder="Add a comment (optional)"
                value={csatComment}
                maxLength={280}
                disabled={csatSubmitting}
                onChange={(e) => setCsatComment(e.target.value)}
                onKeyDown={(e) => {
                  // Enter sends the comment with the chosen score (if any).
                  if (e.key === "Enter" && csatScore) {
                    e.preventDefault()
                    void submitCsat(csatScore)
                  }
                }}
                aria-label="Optional comment"
              />
            </>
          )}
        </div>
      )}

      {/* Persistent input bar — the composer when open. Grows into a rounded
        * card (with an image-preview strip on top) when photos are attached. */}
      <div
        ref={launcherRef}
        className={cn(
          fm.launcher,
          hasMedia && fm["launcher-has-media"],
          // Suppresses the attention pulse once the customer has typed
          // anything — the pulse is a one-time "look here" cue, not a
          // continuous animation. Also kept active by :focus-within in CSS.
          draft.length > 0 && fm["launcher-has-typed"],
          // Attach mode: parent pill swaps color/border/shadow so the
          // morph reads as a deliberate state change, not just a content
          // swap. The class also drives a brief scale-pulse animation.
          attachMenuOpen && fm["launcher-attach-mode"],
        )}
      >
        {hasMedia && (
          <div className={fm["launcher-previews"]}>
            {pendingImages.map((img, i) => (
              <div key={img.url} className={fm["preview-thumb"]}>
                <img src={img.url} alt="Attached" />
                <button
                  type="button"
                  className={fm["preview-remove"]}
                  onClick={() => removeImage(i)}
                  aria-label="Remove image"
                >
                  <XMark />
                </button>
              </div>
            ))}
            {uploading && (
              <div className={cn(fm["preview-thumb"], fm["preview-loading"])}>
                <span className={fm["preview-spinner"]} aria-hidden="true" />
              </div>
            )}
          </div>
        )}
        {/* Attach morph — replaces the launcher row entirely when the ⋯
          * button is tapped. Three type-lanes (photo / video / file),
          * a drag target spanning the whole strip, and a close button.
          * On mobile, drag-and-drop isn't available; the tap-to-pick
          * buttons cover that case (the OS file picker shows the
          * appropriate shortlist based on the accept= filter). */}
        {/* Emoji picker — floats above the composer from the 🙂 button.
          * Uses absolute positioning anchored to the launcher's right edge
          * so the 280px-wide grid never overflows the panel on mobile. */}
        {emojiMenuOpen && (
          <>
            <div
              className={fm["attach-backdrop"]}
              onClick={() => setEmojiMenuOpen(false)}
            />
            <div
              style={{
                position: "absolute",
                right: 6,
                bottom: "calc(100% + 8px)",
                zIndex: 2,
              }}
              role="dialog"
              aria-label="Emoji picker"
            >
              <CuratedEmojiPicker onPick={insertEmojiAtCursor} />
            </div>
          </>
        )}
        {attachMenuOpen ? (
          <div
            className={cn(
              fm["launcher-row"],
              fm["attach-zone"],
              draggingFile && fm["attach-zone-dragging"],
            )}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="region"
            aria-label="Upload"
          >
            <span className={fm["attach-zone-prompt"]} aria-hidden="true">
              <span className={fm["attach-zone-prompt-icon"]}>
                <UploadIcon />
              </span>
              <span className={fm["attach-zone-prompt-text"]}>
                {draggingFile
                  ? "Drop to upload"
                  : "Drop a file or pick one"}
              </span>
            </span>
            <button
              type="button"
              className={fm["attach-zone-quick"]}
              onClick={() => pickFile("image/*")}
              aria-label="Upload a photo"
              title="Photo"
            >
              <ImageIcon />
            </button>
            <button
              type="button"
              className={fm["attach-zone-quick"]}
              onClick={() => pickFile("video/*")}
              aria-label="Upload a video"
              title="Video"
            >
              <VideoIcon />
            </button>
            <button
              type="button"
              className={fm["attach-zone-quick"]}
              onClick={() => pickFile("application/pdf")}
              aria-label="Upload a file"
              title="File"
            >
              <FileIcon />
            </button>
            {/* When images are pending, show a Send button inline so the
              * customer doesn't have to close the attach zone first. Tapping
              * Send calls submitDraft which also collapses the zone. */}
            {pendingImages.length > 0 ? (
              <button
                type="button"
                className={cn(fm["launcher-send"], fm["launcher-send-ready"])}
                onClick={submitDraft}
                aria-label="Send with attachment"
                title="Send"
              >
                <ArrowUp />
              </button>
            ) : (
              <button
                type="button"
                className={fm["launcher-more"]}
                onClick={() => setAttachMenuOpen(false)}
                aria-label="Cancel upload"
                title="Cancel"
              >
                <XMark />
              </button>
            )}
          </div>
        ) : (
          <div className={fm["launcher-row"]}>
            <textarea
              ref={inputRef}
              className={fm["launcher-input"]}
              rows={1}
              placeholder={
                needsIdentityGate && active
                  ? "Enter your name + email above to start"
                  : active
                    ? "Message Pearl…"
                    : // Closed-state launcher: rotate through demo questions
                      // so the empty input looks alive. Stops the moment the
                      // customer focuses or types.
                      animatedPlaceholder
              }
              value={draft}
              onFocus={open}
              onChange={(e) => {
                setDraft(e.target.value)
                autoResize()
                // Throttled customer-typing ping → operator console presence.
                const convId = currentConversationId.current
                if (convId && e.target.value) {
                  const now = Date.now()
                  if (now - lastTypingPingRef.current > 2000) {
                    lastTypingPingRef.current = now
                    fetch(`${props.mountOptions.apiBaseUrl}/api/chat/typing`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ conversationId: convId }),
                      keepalive: true,
                    }).catch(() => {})
                  }
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  submitDraft()
                }
              }}
              // The gate locks the composer ONLY while the panel is open (the
              // form is visibly "above" the input there). In the CLOSED state
              // the input must stay tappable — it's the front door: focusing
              // opens the panel where the gate renders. Disabling it while
              // closed left brand-new visitors with a dead input and a
              // placeholder pointing at a form that wasn't on screen (caught
              // live in the 2026-06-11 design audit — chips were the ONLY
              // way in).
              disabled={needsIdentityGate && active}
              aria-disabled={needsIdentityGate && active}
              aria-label="Message Pearl"
            />
            <button
              type="button"
              className={cn(fm["launcher-more"], emojiMenuOpen && fm["launcher-more-open"])}
              onClick={() => {
                // Mutually exclusive with attach menu — only one popover at a time.
                setAttachMenuOpen(false)
                setEmojiMenuOpen((o) => !o)
              }}
              aria-label="Emoji"
              aria-expanded={emojiMenuOpen}
              disabled={needsIdentityGate && active}
              title="Emoji"
            >
              <SmileyIcon />
            </button>
            <button
              type="button"
              className={cn(fm["launcher-more"], attachMenuOpen && fm["launcher-more-open"])}
              onClick={() => {
                setEmojiMenuOpen(false)
                setAttachMenuOpen((o) => !o)
              }}
              aria-label="Attach"
              aria-expanded={attachMenuOpen}
              disabled={needsIdentityGate && active}
              title="Attach"
            >
              <DotsIcon />
            </button>
            <button
              type="button"
              className={cn(fm["launcher-send"], canSend && fm["launcher-send-ready"])}
              onClick={submitDraft}
              disabled={(needsIdentityGate && active) || (active && !canSend)}
              aria-label={active ? "Send" : "Open chat"}
            >
              <ArrowUp />
            </button>
          </div>
        )}
        {uploadError && <div className={fm["launcher-error"]}>{uploadError}</div>}
        {cartAddError && (
          <div className={fm["launcher-error"]} role="alert">
            Couldn't add that to your cart.{" "}
            <button type="button" className={fm["launcher-error-retry"]} onClick={() => void retryCartAdd()}>
              Retry
            </button>
            <button type="button" className={fm["launcher-error-retry"]} onClick={() => setCartAddError(null)}>
              Dismiss
            </button>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={attachAccept}
          className={fm["file-input"]}
          onChange={onFileChange}
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>
    </div>
    </CartContext.Provider>
  )
}
